import React, { useState, useEffect, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import ReactMarkdown from 'react-markdown';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  CircularProgress,
  Alert,
  Snackbar,
  IconButton,
  Chip,
  Tabs,
  Tab,
  styled,
  TablePagination,
  Autocomplete,
  Link,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Grid,
  InputAdornment,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import SendIcon from '@mui/icons-material/Send';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import SentimentAnalysisIcon from '@mui/icons-material/Psychology';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';

import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import ExclamationIcon from '@mui/icons-material/ReportProblem';
import CloseIcon from '@mui/icons-material/Close';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import AddIcon from '@mui/icons-material/Add';
import AssessmentIcon from '@mui/icons-material/Assessment';
import InfoIcon from '@mui/icons-material/Info';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CalculateIcon from '@mui/icons-material/Calculate';
import ShowChartIcon2 from '@mui/icons-material/ShowChart';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import GavelIcon from '@mui/icons-material/Gavel';
import PeopleIcon from '@mui/icons-material/People';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import LockIcon from '@mui/icons-material/Lock';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import FolderIcon from '@mui/icons-material/Folder';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { environment } from '../../environments/environment';
import axios from 'axios';
import botIcon from '../../assets/Chatbot.svg';
import { Chatbot } from './Chatbot/components/Chatbot';
import { useNavigate } from 'react-router-dom';
import { BarChart, CartesianGrid, XAxis, YAxis, Tooltip as RechartsTooltip, Bar, LineChart, Legend, ResponsiveContainer, Line, PieChart, Pie, Cell, AreaChart, Area, ComposedChart } from 'recharts';
import AISummaryTab from './AISummaryTab';
import rbacApi from '../api/rbacApi';
import GoogleDriveService from '../api/googleDriveService';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import DownloadIcon from '@mui/icons-material/Download';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FileText from '@mui/icons-material/Description';
import Fab from '@mui/material/Fab';

// Interfaces
interface ChatMessage {
  id: number;
  text: string;
  sender: 'user' | 'bot';
  timestamp: Date;
  interactionUID?: string;
  visualization?: {
    type: 'table' | 'chart' | 'heatmap';
    data: MultiColumnData;
  };
}

interface MessageBubbleProps {
  sender: 'user' | 'bot';
  children?: React.ReactNode;
  sx?: any;
}

interface FinancialData {
  groupName: string;
  items: Array<{
    objectType: string;
    docUID: string;
    entries: Array<{
      key: string;
      label: string;
      value: any;
    }>;
  }>;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`financial-tabpanel-${index}`}
      aria-labelledby={`financial-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

// Styled Components
const MessageBubble = styled(Box)<MessageBubbleProps>(({ theme, sender }) => ({
  maxWidth: '70%',
  padding: theme.spacing(1.5, 2),
  borderRadius: sender === 'user' ? '12px 4px 12px 12px' : '4px 12px 12px 12px',
  backgroundColor: sender === 'user' ? '#E3F2FD' : '#F5F5F5',
  marginLeft: sender === 'user' ? 'auto' : '16px',
  marginRight: sender === 'user' ? '16px' : 'auto',
  marginBottom: theme.spacing(1),
  boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
  position: 'relative',
  fontSize: '0.75rem',
  lineHeight: 1.4,
  '&:hover': {
    transform: 'translateY(-1px)',
    transition: 'transform 0.2s ease',
    boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
  }
}));

// Add FullScreenContainer styled component
const FullScreenContainer = styled(Box)({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: '#FFFFFF',
  zIndex: 1300,
  display: 'flex',
  flexDirection: 'column',
  padding: '24px',
  height: '90vh',
  overflow: 'hidden',
});

// Update rating color mapping
const RATING_COLORS = {
  'Medium Risk': '#FFA726',
  'High Risk': '#EF5350',
};

const RELEVANCE_COLORS = {
  High: '#4c3794',
  Medium: '#FFA726',
  Low: '#EF5350',
};

// Add Project interface
interface Project {
  id: string;
  org_id: string;
  project_name: string;
  project_description: string;
  project_type: string;
  configuration: any;
  source: string;
  active: boolean;
  status: string;
  last_updated_date: string;
}

// Add new interfaces for the comprehensive data structure
interface InformationCategory {
  informationCategoryName: string;
  informationDomainName: string;
  informationDomain: string;
  informationCategory: string;
  columns: string[];
  icon: React.ReactElement;
}

interface CategoryData {
  category: string;
  data: any[];
  loading: boolean;
  error: string | null;
}

interface FinancialMetrics {
  [key: string]: number | string;
}

// Export Content interfaces
interface ConversationPair {
  id: string;
  question: {
    id: string;
    content: string;
    timestamp: string;
  };
  answer: {
    id: string;
    content: string;
    timestamp: string;
  };
}

interface BrandDetails {
  website: string;
  websiteNotApplicable: boolean;
  industryKeywords: string;
  targetAudience: string[];
  brandVoice: string;
}

interface ContentPreferences {
  category: string;
  format: string;
  length: string;
  style: string;
  instructions: string;
}

// Add new interfaces for MULTICOLUMN support
interface MultiColumnData {
  headers: string[];
  data: Record<string, any>[];
  title: string;
  visual: boolean;
  dimension: number;
}

interface GeneratedContent {
  title: string;
  content: string;
  format: string;
  category: string;
  followupQuestions?: string[];
  sourceLinks?: string[];
  visualization?: {
    type: 'table' | 'chart' | 'heatmap';
    data: MultiColumnData;
  };
}

// Add the information categories data
const INFORMATION_CATEGORIES: InformationCategory[] = [
  {
    informationCategoryName: "This category includes financial metrics and key performance indicators",
    informationDomainName: "Financial Metrics",
    informationDomain: "FINANCIAL_METRICS",
    informationCategory: "DASHBOARD",
    columns: [
      "REVENUE",
      "EBITDA",
      "NET_PROFIT",
      "RETURN_ON_ASSETS",
      "RETURN_ON_EQUITY",
      "DEBT_TO_EQUITY_RATIO",
      "CURRENT_RATIO",
      "QUICK_RATIO",
      "INTEREST_COVERAGE_RATIO",
      "FIXED_ASSET_TURNOVER_RATIO",
      "GROSS_MARGIN",
      "OPERATING_MARGIN",
      "NET_PROFIT_MARGIN",
      "RETURN_ON_CAPITAL_EMPLOYED"
    ],
    icon: <DashboardIcon sx={{ fontSize: 16 }} />,
  },
  {
    informationCategoryName: "This category contains profit and loss data reported annually in Indian financial statements",
    informationDomainName: "Financial Data",
    informationDomain: "FINANCIAL_DATA",
    informationCategory: "PROFIT_AND_LOSS",
    columns: [
      "NET_REVENUE",
      "TOTAL_INCOME",
      "COST_OF_GOODS_SOLD",
      "EBITDA",
      "PROFIT_BEFORE_TAX",
      "PROFIT_AFTER_TAX",
      "EARNINGS_PER_SHARE_BASIC",
      "EARNINGS_PER_SHARE_DILUTED"
    ],
    icon: <TrendingUpIcon sx={{ fontSize: 16 }} />,
  },
  {
    informationCategoryName: "This category covers financial position details including capital structure as reported in Indian balance sheets",
    informationDomainName: "Financial Data",
    informationDomain: "FINANCIAL_DATA",
    informationCategory: "BALANCE_SHEET",
    columns: [
      "TOTAL_ASSETS",
      "NON_CURRENT_ASSETS",
      "CURRENT_ASSETS",
      "TOTAL_LIABILITIES",
      "NON_CURRENT_LIABILITIES",
      "CURRENT_LIABILITIES",
      "SHARE_CAPITAL",
      "AUTHORIZED_CAPITAL",
      "ISSUED_CAPITAL",
      "PAID_UP_CAPITAL",
      "RESERVES_AND_SURPLUS",
      "BOOK_VALUE_PER_SHARE"
    ],
    icon: <AccountBalanceIcon sx={{ fontSize: 16 }} />,
  },
  {
    informationCategoryName: "This category includes cash flow items reported under Indian Accounting Standards",
    informationDomainName: "Financial Data",
    informationDomain: "FINANCIAL_DATA",
    informationCategory: "CASH_FLOW",
    columns: [
      "CASH_FLOW_FROM_OPERATING_ACTIVITIES",
      "CASH_FLOW_FROM_INVESTING_ACTIVITIES",
      "CASH_FLOW_FROM_FINANCING_ACTIVITIES",
      "CAPITAL_EXPENDITURE",
      "FREE_CASH_FLOW",
      "NET_INCREASE_DECREASE_IN_CASH"
    ],
    icon: <AttachMoneyIcon sx={{ fontSize: 16 }} />,
  },
  {
    informationCategoryName: "This category includes liquidity and solvency ratios commonly used in Indian equity research",
    informationDomainName: "Financial Ratios",
    informationDomain: "FINANCIAL_RATIOS",
    informationCategory: "LIQUIDITY_AND_SOLVENCY",
    columns: [
      "CURRENT_RATIO",
      "QUICK_RATIO",
      "DEBT_TO_EQUITY_RATIO",
      "INTEREST_COVERAGE_RATIO",
      "FIXED_ASSET_TURNOVER_RATIO"
    ],
    icon: <CalculateIcon sx={{ fontSize: 16 }} />,
  },
  {
    informationCategoryName: "This category includes profitability ratios for Indian-listed companies",
    informationDomainName: "Financial Ratios",
    informationDomain: "FINANCIAL_RATIOS",
    informationCategory: "PROFITABILITY",
    columns: [
      "GROSS_MARGIN",
      "OPERATING_MARGIN",
      "NET_PROFIT_MARGIN",
      "RETURN_ON_ASSETS",
      "RETURN_ON_EQUITY",
      "RETURN_ON_CAPITAL_EMPLOYED"
    ],
    icon: <ShowChartIcon2 sx={{ fontSize: 16 }} />,
  },
  {
    informationCategoryName: "This category includes valuation columns used by Indian investors and analysts",
    informationDomainName: "Financial Ratios",
    informationDomain: "FINANCIAL_RATIOS",
    informationCategory: "CASH_AND_VALUATION",
    columns: [
      "DIVIDEND_YIELD",
      "DIVIDEND_PAYOUT_RATIO",
      "PRICE_TO_EARNINGS_RATIO",
      "PRICE_TO_BOOK_RATIO",
      "EV_TO_EBITDA",
      "MARKET_CAP_TO_SALES_RATIO"
    ],
    icon: <MonetizationOnIcon sx={{ fontSize: 16 }} />,
  },
  {
    informationCategoryName: "This category includes stock performance and market-related data for Indian-listed companies",
    informationDomainName: "Market Data",
    informationDomain: "MARKET_DATA",
    informationCategory: "STOCK_PERFORMANCE",
    columns: [
      "STOCK_PRICE",
      "MARKET_CAP",
      "BETA",
      "52_WEEK_HIGH",
      "52_WEEK_LOW",
      "VOLUME_TRADED",
      "LISTING_DATE",
      "STOCK_EXCHANGE"
    ],
    icon: <ShowChartIcon sx={{ fontSize: 16 }} />,
  },
  {
    informationCategoryName: "This category includes company registration and compliance data as per MCA (India)",
    informationDomainName: "Regulatory Information",
    informationDomain: "REGULATORY_INFORMATION",
    informationCategory: "REGISTRATION_AND_FILINGS",
    columns: [
      "CORPORATE_IDENTIFICATION_NUMBER",
      "ROC_NAME",
      "REGISTRATION_NUMBER",
      "INCORPORATION_DATE",
      "COMPANY_TYPE",
      "LISTING_STATUS",
      "ACTIVE_COMPLIANCE_STATUS",
      "AUDITOR_NAME",
      "LAST_AGM_DATE",
      "LAST_BALANCE_SHEET_DATE"
    ],
    icon: <GavelIcon sx={{ fontSize: 16 }} />,
  },
  {
    informationCategoryName: "This category includes peer and market comparison data relevant to Indian equity research",
    informationDomainName: "Competitive Landscape",
    informationDomain: "COMPETITIVE_LANDSCAPE",
    informationCategory: "PEERS_AND_MARKET_POSITION",
    columns: [
      "PRICE_TO_EARNINGS_RATIO",
      "PRICE_TO_BOOK_RATIO",
      "RETURN_ON_EQUITY",
      "EBITDA_MARGIN",
      "NET_PROFIT_MARGIN",
      "DEBT_TO_EQUITY_RATIO",
      "EARNINGS_PER_SHARE",
      "REVENUE_GROWTH_RATE",
      "MARKET_CAP",
      "EV_TO_EBITDA"
    ],
    icon: <BusinessCenterIcon sx={{ fontSize: 16 }} />,
  },
  {
    informationCategoryName: "This category includes corporate actions and statutory disclosures filed with SEBI or exchanges",
    informationDomainName: "Disclosures & Filings",
    informationDomain: "DISCLOSURES",
    informationCategory: "RELATED_PARTY_TRANSACTIONS",
    columns: [
      "TOTAL_RELATED_PARTY_TRANSACTIONS_VALUE",
      "RELATED_PARTY_PURCHASES",
      "RELATED_PARTY_SALES",
      "LOANS_TO_RELATED_PARTIES",
      "REMUNERATION_TO_KEY_MANAGEMENT",
      "GUARANTEES_GIVEN_TO_RELATED_PARTIES"
    ],
    icon: <WarningIcon sx={{ fontSize: 16 }} />,
  },
  {
    informationCategoryName: "This category includes corporate actions and statutory disclosures filed with SEBI or exchanges",
    informationDomainName: "Disclosures & Filings",
    informationDomain: "DISCLOSURES",
    informationCategory: "SHAREHOLDING_PATTERN",
    columns: [
      "PROMOTER_HOLDING_PERCENT",
      "PUBLIC_SHAREHOLDING_PERCENT",
      "INSTITUTIONAL_HOLDING_PERCENT",
      "FOREIGN_PORTFOLIO_INVESTORS_HOLDING_PERCENT",
      "DOMESTIC_INSTITUTIONAL_INVESTORS_HOLDING_PERCENT",
      "RETAIL_SHAREHOLDING_PERCENT",
      "PLEDGED_PROMOTER_SHARES_PERCENT"
    ],
    icon: <PeopleIcon sx={{ fontSize: 16 }} />,
  },
  {
    informationCategoryName: "",
    informationDomainName: "Other",
    informationDomain: "OTHER",
    informationCategory: "OTHER",
    columns: [
      "OTHER"
    ],
    icon: <InfoIcon sx={{ fontSize: 16 }} />,
  }

];

// Helper function to process multi-column data - copied from SurveyInsights.tsx
const processMultiColumnData = (data: any, forChart: boolean = false): MultiColumnData | null => {
  if (!data || !data.headers || !data.data || !Array.isArray(data.data)) {
    return null;
  }

  let cleanHeaders = data.headers;

  // Only filter out problematic columns for charts, not for tables
  if (forChart) {
    // Dynamically detect columns that contain arrays
    const arrayColumns = new Set<string>();

    // Check each row to find columns that contain arrays
    data.data.forEach((row: any) => {
      data.headers.forEach((header: string) => {
        const value = row[header];
        if (Array.isArray(value) && value.length > 1) {
          arrayColumns.add(header);
        }
      });
    });

    // Filter out columns that contain arrays
    cleanHeaders = data.headers.filter((header: string) => !arrayColumns.has(header));
  }

  if (cleanHeaders.length === 0) {
    return null;
  }

  // Process each row to clean the data
  const cleanData = data.data.map((row: any) => {
    const cleanRow: any = {};
    cleanHeaders.forEach((header: string) => {
      const value = row[header];
      if (Array.isArray(value)) {
        if (forChart) {
          // For charts, show count and first item
          if (value.length === 0) {
            cleanRow[header] = 'No data';
          } else if (value.length === 1) {
            cleanRow[header] = String(value[0]);
          } else {
            cleanRow[header] = `${value.length} items`;
          }
        } else {
          // For tables, keep the full array for display
          cleanRow[header] = value;
        }
      } else if (typeof value === 'number') {
        cleanRow[header] = value;
      } else if (typeof value === 'object' && value !== null) {
        cleanRow[header] = forChart ? 'Complex data' : value;
      } else {
        cleanRow[header] = value;
      }
    });
    return cleanRow;
  });

  return {
    headers: cleanHeaders,
    data: cleanData,
    title: data.title || 'Data Analysis',
    visual: data.visual || false,
    dimension: data.dimension || 2
  };
};

const MarkdownMessage = ({ text }: { text: string }) => {
  return (
    <ReactMarkdown
      components={{
        strong: ({ children }) => (
          <Typography component="span" sx={{ fontWeight: 'bold' }}>
            {children}
          </Typography>
        ),
        em: ({ children }) => (
          <Typography component="span" sx={{ fontStyle: 'italic' }}>
            {children}
          </Typography>
        ),
        del: ({ children }) => (
          <Typography component="span" sx={{ textDecoration: 'line-through' }}>
            {children}
          </Typography>
        ),
        p: ({ children }) => (
          <Typography component="span" sx={{ display: 'inline' }}>
            {children}
          </Typography>
        ),
      }}
    >
      {text}
    </ReactMarkdown>
  );
};

// Visualization Components
const DataTable = ({ data }: { data: MultiColumnData }) => {
  // Process data to handle complex arrays and improve display
  const processedData = data.data.map(row => {
    const processedRow: any = {};
    data.headers.forEach(header => {
      const value = row[header];
      if (Array.isArray(value)) {
        // Handle array values - show count and first few items
        if (value.length === 0) {
          processedRow[header] = 'No data';
        } else if (value.length <= 300) {
          processedRow[header] = value; // Keep as array for bullet point rendering
        } else {
          processedRow[header] = `${value.length} items (${value.slice(0, 2).join(', ')}...)`;
        }
      } else if (typeof value === 'number') {
        // Format numbers properly
        processedRow[header] = value.toFixed(2);
      } else if (typeof value === 'object' && value !== null) {
        // Handle object values
        processedRow[header] = JSON.stringify(value).substring(0, 50) + '...';
      } else {
        processedRow[header] = value;
      }
    });
    return processedRow;
  });

  return (
    <Box sx={{
      mt: 2,
      borderRadius: 2,
      overflow: 'hidden',
      border: '1px solid rgba(76, 55, 148, 0.1)',
      backgroundColor: '#fff',
      maxHeight: '400px',
      overflowY: 'auto'
    }}>
      <Box sx={{
        p: 2,
        backgroundColor: 'rgba(76, 55, 148, 0.05)',
        borderBottom: '1px solid rgba(76, 55, 148, 0.1)'
      }}>
        <Typography variant="h6" sx={{
          fontWeight: 600,
          color: '#4c3794',
          fontSize: '0.9rem'
        }}>
          {data.title}
        </Typography>
      </Box>
      <Box sx={{ overflowX: 'auto' }}>
        <table style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: '0.75rem',
          minWidth: '600px'
        }}>
          <thead>
            <tr style={{ backgroundColor: 'rgba(76, 55, 148, 0.05)' }}>
              {data.headers.map((header, index) => (
                <th key={index} style={{
                  padding: '8px 12px',
                  textAlign: 'left',
                  fontWeight: 600,
                  color: '#4c3794',
                  borderBottom: '1px solid rgba(76, 55, 148, 0.1)',
                  fontSize: '0.75rem',
                  whiteSpace: 'nowrap'
                }}>
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {processedData.map((row, rowIndex) => (
              <tr
                key={rowIndex}
                style={{
                  backgroundColor: rowIndex % 2 === 0 ? '#fff' : 'rgba(76, 55, 148, 0.02)'
                }}
              >
                {data.headers.map((header, colIndex) => (
                  <td
                    key={colIndex}
                    style={{
                      padding: '8px 12px',
                      borderBottom: '1px solid rgba(18, 5, 62, 0.05)',
                      color: '#333',
                      fontSize: '0.75rem',
                      maxWidth: '200px',
                      wordWrap: 'break-word',
                      verticalAlign: 'top'
                    }}
                  >
                    {Array.isArray(row[header]) ? (
                      <ul style={{
                        margin: 0,
                        paddingLeft: '16px',
                        listStyleType: 'disc'
                      }}>
                        {row[header].map((item: any, index: number) => (
                          <li key={index} style={{
                            marginBottom: '4px',
                            fontSize: '0.7rem',
                            lineHeight: '1.3'
                          }}>
                            {item}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      row[header]
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </Box>
    </Box>
  );
};

const DataChart = ({ data }: { data: MultiColumnData }) => {
  // Convert data to chart format
  const chartData = data.data.map(row => {
    const chartItem: any = {};
    data.headers.forEach(header => {
      chartItem[header] = row[header];
    });
    return chartItem;
  });

  // Custom tooltip component
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const dataPoint = payload[0].payload;
      return (
        <Box sx={{
          backgroundColor: '#ede7f6',
          borderRadius: '8px',
          border: 'none',
          boxShadow: '0 4px 12px rgba(76, 55, 148, 0.1)',
          padding: '12px 16px',
          color: '#4c3794'
        }}>
          {data.headers.map((header, index) => (
            <Box key={header} sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: index < data.headers.length - 1 ? 1 : 0,
              minWidth: '120px'
            }}>
              <Typography sx={{
                fontWeight: 600,
                fontSize: '0.75rem',
                textTransform: 'capitalize',
                color: '#4c3794'
              }}>
                {header.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}:
              </Typography>
              <Typography sx={{
                fontSize: '0.75rem',
                color: '#2c1810',
                fontWeight: 500
              }}>
                {typeof dataPoint[header] === 'number' && dataPoint[header] % 1 !== 0
                  ? dataPoint[header].toFixed(2)
                  : dataPoint[header]}
              </Typography>
            </Box>
          ))}
        </Box>
      );
    }
    return null;
  };

  // Determine chart type based on dimension
  if (data.dimension === 2) {
    // Bar chart for 2D data
    return (
      <Box sx={{
        mt: 2,
        borderRadius: 2,
        p: 2,
        border: '1px solid rgba(76, 55, 148, 0.1)',
        backgroundColor: '#fff'
      }}>
        <Typography variant="h6" sx={{
          fontWeight: 600,
          color: '#4c3794',
          mb: 2,
          fontSize: '1rem'
        }}>
          {data.title}
        </Typography>
        <ResponsiveContainer width="100%" height={250}>
          <BarChart data={chartData} margin={{ top: 20, right: 30, left: -10, bottom: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#ede7f6" />
            <XAxis
              dataKey={data.headers[0]}
              angle={-45}
              textAnchor="end"
              height={70}
              tick={{ fontSize: 12, fill: '#4c3794' }}
              tickLine={{ stroke: '#b39ddb' }}
            />
            <YAxis
              tick={{ fontSize: 12, fill: '#4c3794' }}
              tickLine={{ stroke: '#b39ddb' }}
            />
            <RechartsTooltip content={<CustomTooltip />} />
            <Bar
              dataKey={data.headers[1]}
              radius={[4, 4, 0, 0]}
              maxBarSize={50}
              fill="#4c3794"
            />
          </BarChart>
        </ResponsiveContainer>
      </Box>
    );
  } else if (data.dimension === 3) {
    // Line chart for 3D data (time series with categories)
    const categories = Array.from(new Set(chartData.map(item => item[data.headers[0]])));

    return (
      <Box sx={{
        mt: 2,
        borderRadius: 2,
        p: 2,
        border: '1px solid rgba(76, 55, 148, 0.1)',
        backgroundColor: '#fff'
      }}>
        <Typography variant="h6" sx={{
          fontWeight: 600,
          color: '#4c3794',
          mb: 2,
          fontSize: '1rem'
        }}>
          {data.title}
        </Typography>
        <ResponsiveContainer width="100%" height={250}>
          <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#ede7f6" />
            <XAxis
              dataKey={data.headers[1]}
              angle={-45}
              textAnchor="end"
              height={70}
              tick={{ fontSize: 12, fill: '#4c3794' }}
              tickLine={{ stroke: '#b39ddb' }}
            />
            <YAxis
              tick={{ fontSize: 12, fill: '#4c3794' }}
              tickLine={{ stroke: '#b39ddb' }}
            />
            <RechartsTooltip content={<CustomTooltip />} />
            <Legend />
            {categories.map((category, index) => {
              const categoryData = chartData.filter(item => item[data.headers[0]] === category);
              return (
                <Line
                  key={category}
                  type="monotone"
                  dataKey={data.headers[2]}
                  data={categoryData}
                  name={category}
                  stroke={`hsl(${240 + index * 60}, 70%, 50%)`}
                  strokeWidth={2}
                  dot={{
                    r: 4,
                    fill: `hsl(${240 + index * 60}, 70%, 50%)`,
                    strokeWidth: 2,
                    stroke: '#fff'
                  }}
                  activeDot={{
                    r: 6,
                    fill: `hsl(${240 + index * 60}, 70%, 50%)`,
                    stroke: '#fff',
                    strokeWidth: 2
                  }}
                />
              );
            })}
          </LineChart>
        </ResponsiveContainer>
      </Box>
    );
  } else {
    // Default to table for other dimensions
    return <DataTable data={data} />;
  }
};

// Dashboard Component
const Dashboard = ({ comprehensiveData, dataFetchProgress }: { comprehensiveData: CategoryData[], dataFetchProgress: { [key: string]: boolean } }) => {
  const getMetricValue = (category: string, metric: string): string | number => {
    const categoryData = comprehensiveData.find(d => d.category === category);
    if (!categoryData?.data || categoryData.data.length === 0) return 'N/A';

    const item = categoryData.data.find((item: any) =>
      item.objectType === 'DICTIONARY' && item.entries?.some((entry: any) => entry.key === metric)
    );

    if (item?.entries) {
      const entry = item.entries.find((entry: any) => entry.key === metric);
      return entry?.value || 'N/A';
    }

    return 'N/A';
  };

  const getMetricColor = (value: string | number): string => {
    if (value === 'N/A') return '#666';
    if (typeof value === 'number') {
      return value > 0 ? '#4c3794' : '#f44336';
    }
    return '#666';
  };

  // Helper function to format currency values
  const formatCurrency = (value: string | number): string => {
    if (value === 'N/A' || typeof value !== 'number') return String(value);
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      notation: 'compact',
      maximumFractionDigits: 1
    }).format(value);
  };

  // Helper function to format percentage values
  const formatPercentage = (value: string | number): string => {
    if (value === 'N/A' || typeof value !== 'number') return String(value);
    return `${value.toFixed(2)}%`;
  };

  // Prepare chart data for Profit & Loss
  const prepareProfitLossData = () => {
    const categoryData = comprehensiveData.find(d => d.category === 'PROFIT_AND_LOSS');
    if (!categoryData?.data || categoryData.data.length === 0) return [];

    // Group data by year and extract metrics
    const yearlyData: { [year: string]: any } = {};

    categoryData.data.forEach((item: any) => {
      if (item.objectType === 'DICTIONARY' && item.entries) {
        // Extract year from entries (assuming there's a year field)
        const yearEntry = item.entries.find((entry: any) =>
          entry.key === 'year' || entry.key === 'financialYear' || entry.key === 'period'
        );
        const year = yearEntry?.value || 'Unknown';

        if (!yearlyData[year]) {
          yearlyData[year] = {};
        }

        // Extract all P&L metrics for this year
        item.entries.forEach((entry: any) => {
          if (['NET_REVENUE', 'TOTAL_INCOME', 'COST_OF_GOODS_SOLD', 'EBITDA', 'PROFIT_BEFORE_TAX', 'PROFIT_AFTER_TAX'].includes(entry.key)) {
            yearlyData[year][entry.key] = entry.value;
          }
        });
      }
    });

    // Convert to chart format
    const chartData = Object.keys(yearlyData).map(year => ({
      year,
      revenue: yearlyData[year].NET_REVENUE || 0,
      totalIncome: yearlyData[year].TOTAL_INCOME || 0,
      cogs: Math.abs(yearlyData[year].COST_OF_GOODS_SOLD || 0),
      ebitda: yearlyData[year].EBITDA || 0,
      pbt: yearlyData[year].PROFIT_BEFORE_TAX || 0,
      pat: yearlyData[year].PROFIT_AFTER_TAX || 0
    })).sort((a, b) => a.year.localeCompare(b.year));

    return chartData;
  };

  // Prepare chart data for Balance Sheet
  const prepareBalanceSheetData = () => {
    const categoryData = comprehensiveData.find(d => d.category === 'BALANCE_SHEET');
    if (!categoryData?.data || categoryData.data.length === 0) return [];

    // Group data by year
    const yearlyData: { [year: string]: any } = {};

    categoryData.data.forEach((item: any) => {
      if (item.objectType === 'DICTIONARY' && item.entries) {
        const yearEntry = item.entries.find((entry: any) =>
          entry.key === 'year' || entry.key === 'financialYear' || entry.key === 'period'
        );
        const year = yearEntry?.value || 'Unknown';

        if (!yearlyData[year]) {
          yearlyData[year] = {};
        }

        item.entries.forEach((entry: any) => {
          if (['TOTAL_ASSETS', 'CURRENT_ASSETS', 'NON_CURRENT_ASSETS', 'TOTAL_LIABILITIES', 'CURRENT_LIABILITIES', 'NON_CURRENT_LIABILITIES', 'SHARE_CAPITAL', 'RESERVES_AND_SURPLUS'].includes(entry.key)) {
            yearlyData[year][entry.key] = entry.value;
          }
        });
      }
    });

    // Convert to chart format
    const chartData = Object.keys(yearlyData).map(year => ({
      year,
      currentAssets: yearlyData[year].CURRENT_ASSETS || 0,
      nonCurrentAssets: yearlyData[year].NON_CURRENT_ASSETS || 0,
      currentLiabilities: yearlyData[year].CURRENT_LIABILITIES || 0,
      nonCurrentLiabilities: yearlyData[year].NON_CURRENT_LIABILITIES || 0,
      shareCapital: yearlyData[year].SHARE_CAPITAL || 0,
      reserves: yearlyData[year].RESERVES_AND_SURPLUS || 0
    })).sort((a, b) => a.year.localeCompare(b.year));

    return chartData;
  };

  // Prepare chart data for Cash Flow
  const prepareCashFlowData = () => {
    const categoryData = comprehensiveData.find(d => d.category === 'CASH_FLOW');
    if (!categoryData?.data || categoryData.data.length === 0) return [];

    // Group data by year
    const yearlyData: { [year: string]: any } = {};

    categoryData.data.forEach((item: any) => {
      if (item.objectType === 'DICTIONARY' && item.entries) {
        const yearEntry = item.entries.find((entry: any) =>
          entry.key === 'year' || entry.key === 'financialYear' || entry.key === 'period'
        );
        const year = yearEntry?.value || 'Unknown';

        if (!yearlyData[year]) {
          yearlyData[year] = {};
        }

        item.entries.forEach((entry: any) => {
          if (['CASH_FLOW_FROM_OPERATING_ACTIVITIES', 'CASH_FLOW_FROM_INVESTING_ACTIVITIES', 'CASH_FLOW_FROM_FINANCING_ACTIVITIES', 'CAPITAL_EXPENDITURE', 'FREE_CASH_FLOW'].includes(entry.key)) {
            yearlyData[year][entry.key] = entry.value;
          }
        });
      }
    });

    // Convert to chart format
    const chartData = Object.keys(yearlyData).map(year => ({
      year,
      operating: yearlyData[year].CASH_FLOW_FROM_OPERATING_ACTIVITIES || 0,
      investing: yearlyData[year].CASH_FLOW_FROM_INVESTING_ACTIVITIES || 0,
      financing: yearlyData[year].CASH_FLOW_FROM_FINANCING_ACTIVITIES || 0,
      capex: Math.abs(yearlyData[year].CAPITAL_EXPENDITURE || 0),
      fcf: yearlyData[year].FREE_CASH_FLOW || 0
    })).sort((a, b) => a.year.localeCompare(b.year));

    return chartData;
  };

  // Prepare chart data for Profitability Ratios
  const prepareProfitabilityData = () => {
    const categoryData = comprehensiveData.find(d => d.category === 'PROFITABILITY');
    if (!categoryData?.data || categoryData.data.length === 0) return [];

    // Group data by year
    const yearlyData: { [year: string]: any } = {};

    categoryData.data.forEach((item: any) => {
      if (item.objectType === 'DICTIONARY' && item.entries) {
        const yearEntry = item.entries.find((entry: any) =>
          entry.key === 'year' || entry.key === 'financialYear' || entry.key === 'period'
        );
        const year = yearEntry?.value || 'Unknown';

        if (!yearlyData[year]) {
          yearlyData[year] = {};
        }

        item.entries.forEach((entry: any) => {
          if (['GROSS_MARGIN', 'OPERATING_MARGIN', 'NET_PROFIT_MARGIN', 'RETURN_ON_ASSETS', 'RETURN_ON_EQUITY', 'RETURN_ON_CAPITAL_EMPLOYED'].includes(entry.key)) {
            yearlyData[year][entry.key] = entry.value;
          }
        });
      }
    });

    // Convert to chart format
    const chartData = Object.keys(yearlyData).map(year => ({
      year,
      grossMargin: yearlyData[year].GROSS_MARGIN || 0,
      operatingMargin: yearlyData[year].OPERATING_MARGIN || 0,
      netMargin: yearlyData[year].NET_PROFIT_MARGIN || 0,
      roa: yearlyData[year].RETURN_ON_ASSETS || 0,
      roe: yearlyData[year].RETURN_ON_EQUITY || 0,
      roce: yearlyData[year].RETURN_ON_CAPITAL_EMPLOYED || 0
    })).sort((a, b) => a.year.localeCompare(b.year));

    return chartData;
  };

  // Prepare chart data for Liquidity & Solvency
  const prepareLiquidityData = () => {
    const categoryData = comprehensiveData.find(d => d.category === 'LIQUIDITY_AND_SOLVENCY');
    if (!categoryData?.data || categoryData.data.length === 0) return [];

    // Group data by year
    const yearlyData: { [year: string]: any } = {};

    categoryData.data.forEach((item: any) => {
      if (item.objectType === 'DICTIONARY' && item.entries) {
        const yearEntry = item.entries.find((entry: any) =>
          entry.key === 'year' || entry.key === 'financialYear' || entry.key === 'period'
        );
        const year = yearEntry?.value || 'Unknown';

        if (!yearlyData[year]) {
          yearlyData[year] = {};
        }

        item.entries.forEach((entry: any) => {
          if (['CURRENT_RATIO', 'QUICK_RATIO', 'DEBT_TO_EQUITY_RATIO', 'INTEREST_COVERAGE_RATIO', 'FIXED_ASSET_TURNOVER_RATIO'].includes(entry.key)) {
            yearlyData[year][entry.key] = entry.value;
          }
        });
      }
    });

    // Convert to chart format
    const chartData = Object.keys(yearlyData).map(year => ({
      year,
      currentRatio: yearlyData[year].CURRENT_RATIO || 0,
      quickRatio: yearlyData[year].QUICK_RATIO || 0,
      debtEquity: yearlyData[year].DEBT_TO_EQUITY_RATIO || 0,
      interestCoverage: yearlyData[year].INTEREST_COVERAGE_RATIO || 0,
      assetTurnover: yearlyData[year].FIXED_ASSET_TURNOVER_RATIO || 0
    })).sort((a, b) => a.year.localeCompare(b.year));

    return chartData;
  };

  // Prepare chart data for Valuation Metrics
  const prepareValuationData = () => {
    const categoryData = comprehensiveData.find(d => d.category === 'CASH_AND_VALUATION');
    if (!categoryData?.data || categoryData.data.length === 0) return [];

    // Group data by year
    const yearlyData: { [year: string]: any } = {};

    categoryData.data.forEach((item: any) => {
      if (item.objectType === 'DICTIONARY' && item.entries) {
        const yearEntry = item.entries.find((entry: any) =>
          entry.key === 'year' || entry.key === 'financialYear' || entry.key === 'period'
        );
        const year = yearEntry?.value || 'Unknown';

        if (!yearlyData[year]) {
          yearlyData[year] = {};
        }

        item.entries.forEach((entry: any) => {
          if (['PRICE_TO_EARNINGS_RATIO', 'PRICE_TO_BOOK_RATIO', 'EV_TO_EBITDA', 'DIVIDEND_YIELD', 'DIVIDEND_PAYOUT_RATIO', 'MARKET_CAP_TO_SALES_RATIO'].includes(entry.key)) {
            yearlyData[year][entry.key] = entry.value;
          }
        });
      }
    });

    // Convert to chart format
    const chartData = Object.keys(yearlyData).map(year => ({
      year,
      pe: yearlyData[year].PRICE_TO_EARNINGS_RATIO || 0,
      pb: yearlyData[year].PRICE_TO_BOOK_RATIO || 0,
      evEbitda: yearlyData[year].EV_TO_EBITDA || 0,
      dividendYield: yearlyData[year].DIVIDEND_YIELD || 0,
      payoutRatio: yearlyData[year].DIVIDEND_PAYOUT_RATIO || 0,
      marketCapSales: yearlyData[year].MARKET_CAP_TO_SALES_RATIO || 0
    })).sort((a, b) => a.year.localeCompare(b.year));

    return chartData;
  };

  // Prepare chart data for Market Performance
  const prepareMarketData = () => {
    const categoryData = comprehensiveData.find(d => d.category === 'STOCK_PERFORMANCE');
    if (!categoryData?.data || categoryData.data.length === 0) return [];

    // Group data by year
    const yearlyData: { [year: string]: any } = {};

    categoryData.data.forEach((item: any) => {
      if (item.objectType === 'DICTIONARY' && item.entries) {
        const yearEntry = item.entries.find((entry: any) =>
          entry.key === 'year' || entry.key === 'financialYear' || entry.key === 'period'
        );
        const year = yearEntry?.value || 'Unknown';

        if (!yearlyData[year]) {
          yearlyData[year] = {};
        }

        item.entries.forEach((entry: any) => {
          if (['STOCK_PRICE', 'MARKET_CAP', 'BETA', '52_WEEK_HIGH', '52_WEEK_LOW', 'VOLUME_TRADED'].includes(entry.key)) {
            yearlyData[year][entry.key] = entry.value;
          }
        });
      }
    });

    // Convert to chart format
    const chartData = Object.keys(yearlyData).map(year => ({
      year,
      stockPrice: yearlyData[year].STOCK_PRICE || 0,
      marketCap: yearlyData[year].MARKET_CAP || 0,
      beta: yearlyData[year].BETA || 0,
      weekHigh: yearlyData[year]['52_WEEK_HIGH'] || 0,
      weekLow: yearlyData[year]['52_WEEK_LOW'] || 0,
      volume: yearlyData[year].VOLUME_TRADED || 0
    })).sort((a, b) => a.year.localeCompare(b.year));

    return chartData;
  };

  // Prepare chart data for Shareholding Pattern
  const prepareShareholdingData = () => {
    const categoryData = comprehensiveData.find(d => d.category === 'SHAREHOLDING_PATTERN');
    if (!categoryData?.data || categoryData.data.length === 0) return [];

    // Group data by year
    const yearlyData: { [year: string]: any } = {};

    categoryData.data.forEach((item: any) => {
      if (item.objectType === 'DICTIONARY' && item.entries) {
        const yearEntry = item.entries.find((entry: any) =>
          entry.key === 'year' || entry.key === 'financialYear' || entry.key === 'period'
        );
        const year = yearEntry?.value || 'Unknown';

        if (!yearlyData[year]) {
          yearlyData[year] = {};
        }

        item.entries.forEach((entry: any) => {
          if (['PROMOTER_HOLDING_PERCENT', 'PUBLIC_SHAREHOLDING_PERCENT', 'INSTITUTIONAL_HOLDING_PERCENT', 'FOREIGN_PORTFOLIO_INVESTORS_HOLDING_PERCENT', 'DOMESTIC_INSTITUTIONAL_INVESTORS_HOLDING_PERCENT', 'RETAIL_SHAREHOLDING_PERCENT', 'PLEDGED_PROMOTER_SHARES_PERCENT'].includes(entry.key)) {
            yearlyData[year][entry.key] = entry.value;
          }
        });
      }
    });

    // Convert to chart format
    const chartData = Object.keys(yearlyData).map(year => ({
      year,
      promoterHolding: yearlyData[year].PROMOTER_HOLDING_PERCENT || 0,
      publicHolding: yearlyData[year].PUBLIC_SHAREHOLDING_PERCENT || 0,
      institutionalHolding: yearlyData[year].INSTITUTIONAL_HOLDING_PERCENT || 0,
      fpiHolding: yearlyData[year].FOREIGN_PORTFOLIO_INVESTORS_HOLDING_PERCENT || 0,
      diiHolding: yearlyData[year].DOMESTIC_INSTITUTIONAL_INVESTORS_HOLDING_PERCENT || 0,
      retailHolding: yearlyData[year].RETAIL_SHAREHOLDING_PERCENT || 0,
      pledgedShares: yearlyData[year].PLEDGED_PROMOTER_SHARES_PERCENT || 0
    })).sort((a, b) => a.year.localeCompare(b.year));

    return chartData;
  };

  // Prepare chart data for Peer Comparison
  const preparePeerComparisonData = () => {
    const categoryData = comprehensiveData.find(d => d.category === 'PEERS_AND_MARKET_POSITION');
    if (!categoryData?.data || categoryData.data.length === 0) return [];

    // Group data by year
    const yearlyData: { [year: string]: any } = {};

    categoryData.data.forEach((item: any) => {
      if (item.objectType === 'DICTIONARY' && item.entries) {
        const yearEntry = item.entries.find((entry: any) =>
          entry.key === 'year' || entry.key === 'financialYear' || entry.key === 'period'
        );
        const year = yearEntry?.value || 'Unknown';

        if (!yearlyData[year]) {
          yearlyData[year] = {};
        }

        item.entries.forEach((entry: any) => {
          if (['PRICE_TO_EARNINGS_RATIO', 'PRICE_TO_BOOK_RATIO', 'RETURN_ON_EQUITY', 'EBITDA_MARGIN', 'NET_PROFIT_MARGIN', 'DEBT_TO_EQUITY_RATIO', 'EARNINGS_PER_SHARE', 'REVENUE_GROWTH_RATE', 'MARKET_CAP', 'EV_TO_EBITDA'].includes(entry.key)) {
            yearlyData[year][entry.key] = entry.value;
          }
        });
      }
    });

    // Convert to chart format
    const chartData = Object.keys(yearlyData).map(year => ({
      year,
      pe: yearlyData[year].PRICE_TO_EARNINGS_RATIO || 0,
      pb: yearlyData[year].PRICE_TO_BOOK_RATIO || 0,
      roe: yearlyData[year].RETURN_ON_EQUITY || 0,
      ebitdaMargin: yearlyData[year].EBITDA_MARGIN || 0,
      netMargin: yearlyData[year].NET_PROFIT_MARGIN || 0,
      debtEquity: yearlyData[year].DEBT_TO_EQUITY_RATIO || 0,
      eps: yearlyData[year].EARNINGS_PER_SHARE || 0,
      revenueGrowth: yearlyData[year].REVENUE_GROWTH_RATE || 0,
      marketCap: yearlyData[year].MARKET_CAP || 0,
      evEbitda: yearlyData[year].EV_TO_EBITDA || 0
    })).sort((a, b) => a.year.localeCompare(b.year));

    return chartData;
  };

  return (
    <Box>


      <Grid container spacing={3}>
        {/* Key Metrics Cards */}
        <Grid item xs={12} md={6} lg={3}>
          <Card sx={{
            height: '100%',
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
            }
          }}>
            <CardContent sx={{ textAlign: 'center', p: 3 }}>
              <TrendingUpIcon sx={{ fontSize: 40, color: '#4c3794', mb: 2 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                Revenue
              </Typography>
              <Typography variant="h4" sx={{
                fontWeight: 700,
                color: getMetricColor(getMetricValue('PROFIT_AND_LOSS', 'NET_REVENUE'))
              }}>
                {formatCurrency(getMetricValue('PROFIT_AND_LOSS', 'NET_REVENUE'))}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6} lg={3}>
          <Card sx={{
            height: '100%',
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
            }
          }}>
            <CardContent sx={{ textAlign: 'center', p: 3 }}>
              <AttachMoneyIcon sx={{ fontSize: 40, color: '#4c3794', mb: 2 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                EBITDA
              </Typography>
              <Typography variant="h4" sx={{
                fontWeight: 700,
                color: getMetricColor(getMetricValue('PROFIT_AND_LOSS', 'EBITDA'))
              }}>
                {formatCurrency(getMetricValue('PROFIT_AND_LOSS', 'EBITDA'))}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6} lg={3}>
          <Card sx={{
            height: '100%',
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
            }
          }}>
            <CardContent sx={{ textAlign: 'center', p: 3 }}>
              <CalculateIcon sx={{ fontSize: 40, color: '#4c3794', mb: 2 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                Debt/Equity
              </Typography>
              <Typography variant="h4" sx={{
                fontWeight: 700,
                color: getMetricColor(getMetricValue('LIQUIDITY_AND_SOLVENCY', 'DEBT_TO_EQUITY_RATIO'))
              }}>
                {formatPercentage(getMetricValue('LIQUIDITY_AND_SOLVENCY', 'DEBT_TO_EQUITY_RATIO'))}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6} lg={3}>
          <Card sx={{
            height: '100%',
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
            }
          }}>
            <CardContent sx={{ textAlign: 'center', p: 3 }}>
              <ShowChartIcon sx={{ fontSize: 40, color: '#4c3794', mb: 2 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                ROE
              </Typography>
              <Typography variant="h4" sx={{
                fontWeight: 700,
                color: getMetricColor(getMetricValue('PROFITABILITY', 'RETURN_ON_EQUITY'))
              }}>
                {formatPercentage(getMetricValue('PROFITABILITY', 'RETURN_ON_EQUITY'))}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Profit & Loss Chart */}
        <Grid item xs={12}>
          <Card sx={{
            height: 400,
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
            }
          }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1a1a1a' }}>
                Profit & Loss Analysis (Yearly Trend)
              </Typography>
              {dataFetchProgress['PROFIT_AND_LOSS'] ? (
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: 300,
                  flexDirection: 'column',
                  gap: 2
                }}>
                  <CircularProgress sx={{ color: '#4c3794' }} />
                  <Typography sx={{ color: '#666', fontSize: '0.875rem' }}>
                    Loading profit & loss data...
                  </Typography>
                </Box>
              ) : prepareProfitLossData().length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={prepareProfitLossData()} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis
                      dataKey="year"
                      angle={-45}
                      textAnchor="end"
                      height={80}
                      tick={{ fontSize: 12, fill: '#666' }}
                    />
                    <YAxis
                      tick={{ fontSize: 12, fill: '#666' }}
                      tickFormatter={(value) => formatCurrency(value)}
                    />
                    <RechartsTooltip
                      formatter={(value: any) => [formatCurrency(value), '']}
                      labelFormatter={(label) => `Year: ${label}`}
                    />
                    <Legend />
                    <Line type="monotone" dataKey="revenue" stroke="#4c3794" strokeWidth={2} name="Revenue" />
                    <Line type="monotone" dataKey="totalIncome" stroke="#6a4caf" strokeWidth={2} name="Total Income" />
                    <Line type="monotone" dataKey="ebitda" stroke="#8b6fc7" strokeWidth={2} name="EBITDA" />
                    <Line type="monotone" dataKey="pbt" stroke="#a084e8" strokeWidth={2} name="PBT" />
                    <Line type="monotone" dataKey="pat" stroke="#607D8B" strokeWidth={2} name="PAT" />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: 300,
                  color: '#666'
                }}>
                  <Typography>No profit & loss data available</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Balance Sheet Chart */}
        <Grid item xs={12}>
          <Card sx={{
            height: 400,
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
            }
          }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1a1a1a' }}>
                Balance Sheet Composition (Yearly Trend)
              </Typography>
              {dataFetchProgress['BALANCE_SHEET'] ? (
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: 300,
                  flexDirection: 'column',
                  gap: 2
                }}>
                  <CircularProgress sx={{ color: '#4c3794' }} />
                  <Typography sx={{ color: '#666', fontSize: '0.875rem' }}>
                    Loading balance sheet data...
                  </Typography>
                </Box>
              ) : prepareBalanceSheetData().length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={prepareBalanceSheetData()} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis
                      dataKey="year"
                      angle={-45}
                      textAnchor="end"
                      height={80}
                      tick={{ fontSize: 12, fill: '#666' }}
                    />
                    <YAxis
                      tick={{ fontSize: 12, fill: '#666' }}
                      tickFormatter={(value) => formatCurrency(value)}
                    />
                    <RechartsTooltip
                      formatter={(value: any) => [formatCurrency(value), '']}
                      labelFormatter={(label) => `Year: ${label}`}
                    />
                    <Legend />
                    <Area type="monotone" dataKey="currentAssets" stackId="1" stroke="#4c3794" fill="#4c3794" fillOpacity={0.6} name="Current Assets" />
                    <Area type="monotone" dataKey="nonCurrentAssets" stackId="1" stroke="#6a4caf" fill="#6a4caf" fillOpacity={0.6} name="Non-Current Assets" />
                    <Area type="monotone" dataKey="currentLiabilities" stackId="2" stroke="#FF5722" fill="#FF5722" fillOpacity={0.6} name="Current Liabilities" />
                    <Area type="monotone" dataKey="nonCurrentLiabilities" stackId="2" stroke="#FF9800" fill="#FF9800" fillOpacity={0.6} name="Non-Current Liabilities" />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: 300,
                  color: '#666'
                }}>
                  <Typography>No balance sheet data available</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Cash Flow Chart */}
        <Grid item xs={12}>
          <Card sx={{
            height: 400,
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
            }
          }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1a1a1a' }}>
                Cash Flow Analysis (Yearly Trend)
              </Typography>
              {dataFetchProgress['CASH_FLOW'] ? (
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: 300,
                  flexDirection: 'column',
                  gap: 2
                }}>
                  <CircularProgress sx={{ color: '#4c3794' }} />
                  <Typography sx={{ color: '#666', fontSize: '0.875rem' }}>
                    Loading cash flow data...
                  </Typography>
                </Box>
              ) : prepareCashFlowData().length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <ComposedChart data={prepareCashFlowData()} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis
                      dataKey="year"
                      angle={-45}
                      textAnchor="end"
                      height={80}
                      tick={{ fontSize: 12, fill: '#666' }}
                    />
                    <YAxis
                      tick={{ fontSize: 12, fill: '#666' }}
                      tickFormatter={(value) => formatCurrency(value)}
                    />
                    <RechartsTooltip
                      formatter={(value: any) => [formatCurrency(value), '']}
                      labelFormatter={(label) => `Year: ${label}`}
                    />
                    <Legend />
                    <Bar dataKey="operating" fill="#4c3794" name="Operating CF" />
                    <Bar dataKey="investing" fill="#6a4caf" name="Investing CF" />
                    <Bar dataKey="financing" fill="#8b6fc7" name="Financing CF" />
                    <Line type="monotone" dataKey="fcf" stroke="#a084e8" strokeWidth={3} name="Free Cash Flow" />
                  </ComposedChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: 300,
                  color: '#666'
                }}>
                  <Typography>No cash flow data available</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Profitability Ratios Chart */}
        <Grid item xs={12}>
          <Card sx={{
            height: 400,
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
            }
          }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1a1a1a' }}>
                Profitability Ratios (Yearly Trend)
              </Typography>
              {dataFetchProgress['PROFITABILITY'] ? (
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: 300,
                  flexDirection: 'column',
                  gap: 2
                }}>
                  <CircularProgress sx={{ color: '#4c3794' }} />
                  <Typography sx={{ color: '#666', fontSize: '0.875rem' }}>
                    Loading profitability data...
                  </Typography>
                </Box>
              ) : prepareProfitabilityData().length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={prepareProfitabilityData()} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis
                      dataKey="year"
                      angle={-45}
                      textAnchor="end"
                      height={80}
                      tick={{ fontSize: 12, fill: '#666' }}
                    />
                    <YAxis
                      tick={{ fontSize: 12, fill: '#666' }}
                      tickFormatter={(value) => `${value}%`}
                    />
                    <RechartsTooltip
                      formatter={(value: any) => [`${value}%`, '']}
                      labelFormatter={(label) => `Year: ${label}`}
                    />
                    <Legend />
                    <Line type="monotone" dataKey="grossMargin" stroke="#4c3794" strokeWidth={2} name="Gross Margin" />
                    <Line type="monotone" dataKey="operatingMargin" stroke="#6a4caf" strokeWidth={2} name="Operating Margin" />
                    <Line type="monotone" dataKey="netMargin" stroke="#8b6fc7" strokeWidth={2} name="Net Margin" />
                    <Line type="monotone" dataKey="roa" stroke="#a084e8" strokeWidth={2} name="ROA" />
                    <Line type="monotone" dataKey="roe" stroke="#607D8B" strokeWidth={2} name="ROE" />
                    <Line type="monotone" dataKey="roce" stroke="#795548" strokeWidth={2} name="ROCE" />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: 300,
                  color: '#666'
                }}>
                  <Typography>No profitability data available</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Shareholding Pattern Chart */}
        <Grid item xs={12}>
          <Card sx={{
            height: 400,
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
            }
          }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1a1a1a' }}>
                Shareholding Pattern (Yearly Trend)
              </Typography>
              {dataFetchProgress['SHAREHOLDING_PATTERN'] ? (
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: 300,
                  flexDirection: 'column',
                  gap: 2
                }}>
                  <CircularProgress sx={{ color: '#4c3794' }} />
                  <Typography sx={{ color: '#666', fontSize: '0.875rem' }}>
                    Loading shareholding data...
                  </Typography>
                </Box>
              ) : prepareShareholdingData().length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={prepareShareholdingData()} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis
                      dataKey="year"
                      angle={-45}
                      textAnchor="end"
                      height={80}
                      tick={{ fontSize: 12, fill: '#666' }}
                    />
                    <YAxis
                      tick={{ fontSize: 12, fill: '#666' }}
                      tickFormatter={(value) => `${value}%`}
                    />
                    <RechartsTooltip
                      formatter={(value: any) => [`${value}%`, '']}
                      labelFormatter={(label) => `Year: ${label}`}
                    />
                    <Legend />
                    <Area type="monotone" dataKey="promoterHolding" stackId="1" stroke="#4c3794" fill="#4c3794" fillOpacity={0.6} name="Promoter Holding" />
                    <Area type="monotone" dataKey="publicHolding" stackId="1" stroke="#6a4caf" fill="#6a4caf" fillOpacity={0.6} name="Public Holding" />
                    <Area type="monotone" dataKey="institutionalHolding" stackId="1" stroke="#8b6fc7" fill="#8b6fc7" fillOpacity={0.6} name="Institutional" />
                    <Area type="monotone" dataKey="fpiHolding" stackId="1" stroke="#a084e8" fill="#a084e8" fillOpacity={0.6} name="FPI" />
                    <Area type="monotone" dataKey="diiHolding" stackId="1" stroke="#607D8B" fill="#607D8B" fillOpacity={0.6} name="DII" />
                    <Area type="monotone" dataKey="retailHolding" stackId="1" stroke="#795548" fill="#795548" fillOpacity={0.6} name="Retail" />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: 300,
                  color: '#666'
                }}>
                  <Typography>No shareholding data available</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>


      </Grid>
    </Box>
  );
};

const DataHeatmap = ({ data }: { data: MultiColumnData }) => {
  // For 3D data, create a heatmap visualization
  const [hoveredCell, setHoveredCell] = useState<{
    x: any;
    y: any;
    value: any;
    xPos: number;
    yPos: number;
  } | null>(null);

  const prepareHeatmapData = (): { data: any[]; xValues: any[]; yValues: any[]; zHeader: string } | null => {
    if (!data.data || data.data.length === 0) return null;

    // For 3D data, we'll create a heatmap where:
    // - X-axis: First header (e.g., location, category)
    // - Y-axis: Second header (e.g., time period, subcategory) 
    // - Color intensity: Third header (e.g., sentiment score, count)

    const xValues = Array.from(new Set(data.data.map(row => row[data.headers[0]])));
    const yValues = Array.from(new Set(data.data.map(row => row[data.headers[1]])));

    // Create a matrix for the heatmap
    const heatmapData = yValues.map(yValue => {
      const row: any = { y: yValue };
      xValues.forEach(xValue => {
        const matchingRow = data.data.find(row =>
          row[data.headers[0]] === xValue && row[data.headers[1]] === yValue
        );
        row[xValue] = matchingRow ? matchingRow[data.headers[2]] : 0;
      });
      return row;
    });

    return {
      data: heatmapData,
      xValues,
      yValues,
      zHeader: data.headers[2]
    };
  };

  const heatmapConfig = prepareHeatmapData();

  // Get min and max values for color scaling
  const allValues = data.data.map(row => row[data.headers[2]]).filter(val => typeof val === 'number');
  const minValue = Math.min(...allValues);
  const maxValue = Math.max(...allValues);

  const getColorIntensity = (value: number) => {
    if (typeof value !== 'number') return '#f0f0f0';
    const normalized = (value - minValue) / (maxValue - minValue);
    // Use a color gradient from light purple to dark purple to match theme
    const intensity = Math.floor(normalized * 255);
    return `rgb(${237 + intensity * 0.07}, ${231 + intensity * 0.09}, ${246 + intensity * 0.03})`;
  };

  const getTextColor = (backgroundColor: string) => {
    // Extract RGB values from the background color
    const rgbMatch = backgroundColor.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
    if (!rgbMatch) return '#333';

    const r = parseInt(rgbMatch[1]);
    const g = parseInt(rgbMatch[2]);
    const b = parseInt(rgbMatch[3]);

    // Calculate luminance
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

    // Use dark text for light backgrounds, light text for dark backgrounds
    return luminance > 0.6 ? '#333' : '#fff';
  };

  return (
    <Box sx={{
      mt: 2,
      borderRadius: 2,
      p: 2,
      border: '1px solid rgba(76, 55, 148, 0.1)',
      backgroundColor: '#fff'
    }}>
      <Typography variant="h6" sx={{
        fontWeight: 600,
        color: '#4c3794',
        mb: 2,
        fontSize: '1rem'
      }}>
        {data.title} - Heatmap Visualization
      </Typography>

      {heatmapConfig && heatmapConfig.data.length > 0 ? (
        <Box sx={{ overflowX: 'auto' }}>
          <Box sx={{
            display: 'inline-block',
            minWidth: '100%',
            border: '1px solid #e0e0e0',
            borderRadius: 1,
            overflow: 'hidden'
          }}>
            {/* Header row with X values */}
            <Box sx={{
              display: 'flex',
              borderBottom: '2px solid #4c3794',
              backgroundColor: '#f8f9fa'
            }}>
              <Box sx={{
                width: 120,
                p: 1,
                borderRight: '2px solid #4c3794',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 600,
                fontSize: '0.75rem',
                color: '#4c3794'
              }}>
                {data.headers[1]} / {data.headers[0]}
              </Box>
              {heatmapConfig.xValues.map((xValue: any, index: number) => (
                <Box key={index} sx={{
                  flex: 1,
                  p: 1,
                  textAlign: 'center',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  color: '#4c3794',
                  borderRight: index < heatmapConfig.xValues.length - 1 ? '1px solid #e0e0e0' : 'none'
                }}>
                  {String(xValue)}
                </Box>
              ))}
            </Box>

            {/* Data rows */}
            {heatmapConfig.data.map((row: any, rowIndex: number) => (
              <Box key={rowIndex} sx={{
                display: 'flex',
                borderBottom: rowIndex < heatmapConfig.data.length - 1 ? '1px solid #e0e0e0' : 'none'
              }}>
                <Box sx={{
                  width: 120,
                  p: 1,
                  borderRight: '2px solid #4c3794',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  color: '#4c3794',
                  backgroundColor: '#f8f9fa'
                }}>
                  {String(row.y)}
                </Box>
                {heatmapConfig.xValues.map((xValue: any, colIndex: number) => {
                  const backgroundColor = getColorIntensity(row[xValue]);
                  const textColor = getTextColor(backgroundColor);

                  return (
                    <Box key={colIndex} sx={{
                      flex: 1,
                      p: 1,
                      textAlign: 'center',
                      fontSize: '0.75rem',
                      backgroundColor,
                      color: textColor,
                      fontWeight: 500,
                      borderRight: colIndex < heatmapConfig.xValues.length - 1 ? '1px solid #e0e0e0' : 'none',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        transform: 'scale(1.05)',
                        boxShadow: '0 2px 8px rgba(76, 55, 148, 0.2)',
                        zIndex: 1,
                        position: 'relative'
                      }
                    }}
                      onMouseEnter={(e) => {
                        const cellData = {
                          x: xValue,
                          y: row.y,
                          value: row[xValue],
                          xPos: e.clientX,
                          yPos: e.clientY
                        };

                        setHoveredCell(cellData);
                      }}
                      onMouseLeave={() => {

                        setHoveredCell(null);
                      }}
                    >
                      {typeof row[xValue] === 'number' ? (row[xValue] % 1 === 0 ? row[xValue].toString() : row[xValue].toFixed(2)) : row[xValue]}
                    </Box>
                  );
                })}
              </Box>
            ))}
          </Box>

          {/* Color legend */}
          <Box sx={{
            mt: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            justifyContent: 'center'
          }}>
            <Typography variant="caption" sx={{ color: '#4c3794', fontWeight: 600, fontSize: '0.75rem' }}>
              {data.headers[2]} Scale:
            </Typography>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              height: 24,
              background: 'linear-gradient(to right, rgb(237, 231, 246), rgb(76, 55, 148))',
              borderRadius: 1,
              px: 2,
              border: '1px solid rgba(76, 55, 148, 0.1)'
            }}>
              <Typography variant="caption" sx={{ color: '#4c3794', fontWeight: 600, fontSize: '0.7rem' }}>
                {minValue.toFixed(2)}
              </Typography>
              <Typography variant="caption" sx={{ color: '#4c3794', fontSize: '0.7rem' }}>
                →
              </Typography>
              <Typography variant="caption" sx={{ color: '#4c3794', fontWeight: 600, fontSize: '0.7rem' }}>
                {maxValue.toFixed(2)}
              </Typography>
            </Box>
          </Box>
        </Box>
      ) : (
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: 200,
          color: '#666',
          backgroundColor: '#f8f9fa',
          borderRadius: 1
        }}>
          <Typography>No data available for heatmap visualization</Typography>
        </Box>
      )}

      {/* Custom tooltip using portal */}
      {hoveredCell && ReactDOM.createPortal(
        <Box
          sx={{
            position: 'fixed',
            left: hoveredCell.xPos + 10,
            top: hoveredCell.yPos - 10,
            backgroundColor: '#ede7f6',
            borderRadius: '8px',
            border: '2px solid #4c3794',
            boxShadow: '0 4px 12px rgba(76, 55, 148, 0.25)',
            padding: '12px 16px',
            color: '#4c3794',
            zIndex: 9999,
            minWidth: '180px',
            backdropFilter: 'blur(8px)',
            pointerEvents: 'none',
            '&::before': {
              content: '""',
              position: 'absolute',
              left: '-6px',
              top: '50%',
              transform: 'translateY(-50%)',
              width: 0,
              height: 0,
              borderTop: '6px solid transparent',
              borderBottom: '6px solid transparent',
              borderRight: '6px solid #4c3794'
            }
          }}
        >
          <Box sx={{ mb: 1 }}>
            <Typography sx={{
              fontWeight: 600,
              fontSize: '0.75rem',
              textTransform: 'capitalize',
              color: '#4c3794',
              mb: 0.5
            }}>
              {data.headers[0]}:
            </Typography>
            <Typography sx={{
              fontSize: '0.75rem',
              color: '#2c1810',
              fontWeight: 500
            }}>
              {String(hoveredCell.x)}
            </Typography>
          </Box>
          <Box sx={{ mb: 1 }}>
            <Typography sx={{
              fontWeight: 600,
              fontSize: '0.75rem',
              textTransform: 'capitalize',
              color: '#4c3794',
              mb: 0.5
            }}>
              {data.headers[1]}:
            </Typography>
            <Typography sx={{
              fontSize: '0.75rem',
              color: '#2c1810',
              fontWeight: 500
            }}>
              {String(hoveredCell.y)}
            </Typography>
          </Box>
          <Box>
            <Typography sx={{
              fontWeight: 600,
              fontSize: '0.75rem',
              textTransform: 'capitalize',
              color: '#4c3794',
              mb: 0.5
            }}>
              {data.headers[2]}:
            </Typography>
            <Typography sx={{
              fontSize: '0.75rem',
              color: '#2c1810',
              fontWeight: 500
            }}>
              {typeof hoveredCell.value === 'number' ? (hoveredCell.value % 1 === 0 ? hoveredCell.value.toString() : hoveredCell.value.toFixed(2)) : String(hoveredCell.value)}
            </Typography>
          </Box>
        </Box>,
        document.body
      )}
    </Box>
  );
};

const CreditChecker = () => {
  const navigate = useNavigate();
  // State management
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [financialData, setFinancialData] = useState<FinancialData[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(3);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);

  // Ref to track previous campaignId to prevent unnecessary Chatbot resets
  const previousCampaignIdRef = useRef<string>('');

  // Ref to track current project ID to detect project changes
  const currentProjectIdRef = useRef<string | null>(null);

  // Ref to store the apiConfig object to prevent unnecessary Chatbot resets
  const chatbotApiConfigRef = useRef<{
    baseUrl: string;
    queryEndpoint: string;
    campaignId: string;
    agentCode: string;
    contentTypesEndpoint: string;
    generateContentEndpoint: string;
    customQueryParams: Record<string, any>;
  }>({
    baseUrl: environment.controlplaneUrl,
    queryEndpoint: '/agent/flow/execute',
    campaignId: '',
    agentCode: 'QUERY_RESEARCH_INTEL_AND_RESPOND',
    contentTypesEndpoint: '/account/getContentTypes',
    generateContentEndpoint: '/agent/flow/execute',
    customQueryParams: {}
  });

  // Memoize apiConfig to prevent unnecessary Chatbot resets
  // Only update when campaignId actually changes
  const currentCampaignId = selectedProject?.id || '';
  const chatbotApiConfig = useMemo(() => {
    // Only create new object if campaignId actually changed
    if (previousCampaignIdRef.current === currentCampaignId) {
      // Return previous config object to prevent unnecessary resets
      return chatbotApiConfigRef.current;
    }

    // CampaignId changed, update refs and return new config
    previousCampaignIdRef.current = currentCampaignId;
    const newConfig = {
      baseUrl: environment.controlplaneUrl,
      queryEndpoint: '/agent/flow/execute',
      campaignId: currentCampaignId,
      agentCode: 'QUERY_RESEARCH_INTEL_AND_RESPOND',
      contentTypesEndpoint: '/account/getContentTypes',
      generateContentEndpoint: '/agent/flow/execute',
      customQueryParams: {}
    };
    chatbotApiConfigRef.current = newConfig;
    return newConfig;
  }, [currentCampaignId]);
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);
  const [isProjectDialogOpen, setIsProjectDialogOpen] = useState(false);
  const [newProject, setNewProject] = useState({
    project_name: '',
    project_description: '',
    project_type: 'CMPN_RSRC',
    period_start_date: new Date().toISOString().split('T')[0],
    period_end_date: new Date().toISOString().split('T')[0],
    period_type: 'daily',
    source: 'manual',
    active: true,
    status: 'active'
  });
  const [isSubmittingProject, setIsSubmittingProject] = useState(false);
  const [projectError, setProjectError] = useState('');
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  // RBAC permission state
  const [canEditProject, setCanEditProject] = useState(false);
  const [canViewProject, setCanViewProject] = useState(true);
  const [loadingPermissions, setLoadingPermissions] = useState(false);

  // Add comprehensive data state
  const [comprehensiveData, setComprehensiveData] = useState<CategoryData[]>([]);
  const [isLoadingComprehensiveData, setIsLoadingComprehensiveData] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>(''); // Will be set dynamically
  const [showComprehensiveView, setShowComprehensiveView] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [dataFetchProgress, setDataFetchProgress] = useState<{ [key: string]: boolean }>({});

  // Add top-level tab state
  const [topLevelTab, setTopLevelTab] = useState(0); // 0: Dashboard, 1: AI Summary, 2: Data Categories

  // Initial loading state to track all API calls
  const [initialLoadingState, setInitialLoadingState] = useState({
    projects: false,
    comprehensiveData: false,
    permissions: false,
    contentTypes: false
  });

  // Compute overall initial loading status
  const isInitialLoading = useMemo(() => {
    // Only show loader on Dashboard tab
    if (topLevelTab !== 0) return false;

    // Only show loader if we have a project selected (after initial projects load)
    // This prevents showing loader before project is selected
    if (!selectedProject && initialLoadingState.projects) {
      return true; // Still loading projects
    }

    if (!selectedProject) return false; // No project selected, don't show loader

    // Show loader only for APIs essential for dashboard display
    // contentTypes is not needed for dashboard, so we exclude it
    return (
      initialLoadingState.comprehensiveData ||
      initialLoadingState.permissions
    );
  }, [topLevelTab, initialLoadingState, selectedProject]);

  // Export functionality state - copied from SurveyInsights.tsx
  const [isExportMode, setIsExportMode] = useState(false);
  const [selectedPairs, setSelectedPairs] = useState<Set<string>>(new Set());
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [brandDetails, setBrandDetails] = useState<BrandDetails>({
    website: '',
    websiteNotApplicable: false,
    industryKeywords: '',
    targetAudience: [],
    brandVoice: 'professional'
  });
  const [contentPreferences, setContentPreferences] = useState<ContentPreferences>({
    category: '',
    format: 'Article',
    length: 'Medium',
    style: 'Professional',
    instructions: ''
  });
  const [newAudience, setNewAudience] = useState('');
  const [showAudienceDropdown, setShowAudienceDropdown] = useState(false);
  const [showContentDisplay, setShowContentDisplay] = useState(false);
  const [generatedContent, setGeneratedContent] = useState<GeneratedContent | null>(null);
  const [isGeneratingContent, setIsGeneratingContent] = useState(false);
  const [isExportingAsIs, setIsExportingAsIs] = useState(false);
  const [contentTypes, setContentTypes] = useState<any[]>([]);

  // Google Drive integration state
  const [showGoogleDriveDialog, setShowGoogleDriveDialog] = useState(false);
  const [googleDriveAuthState, setGoogleDriveAuthState] = useState({
    isAuthenticated: false,
    sessionId: '',
    loading: false,
    error: null as string | null,
  });
  const [googleDriveFiles, setGoogleDriveFiles] = useState<any[]>([]);
  const [googleDriveFolders, setGoogleDriveFolders] = useState<any[]>([]);
  const [googleDriveSearchQuery, setGoogleDriveSearchQuery] = useState('');
  const [googleDriveService] = useState(() => new GoogleDriveService({
    onFilesLoaded: (files: any[]) => setGoogleDriveFiles(files),
    onLoadingStateChange: (loading: boolean) => setGoogleDriveAuthState(prev => ({ ...prev, loading })),
    onAuthStateChange: (state: any) => {
      setGoogleDriveAuthState(prev => ({ ...prev, isAuthenticated: state.isAuthenticated, sessionId: state.sessionId }));
    },
    onError: (error: string) => {
      setGoogleDriveAuthState(prev => ({ ...prev, error }));
    },
    onSuccess: (message: string) => {
      setSuccess(message);
    },
    onDownloadProgress: () => {
      // Handle download progress if needed
    },
    onFolderBreadcrumbLoaded: (breadcrumb: any) => {
      // Handle folder breadcrumb if needed
    },
    onFilePreviewLoaded: (preview: any) => {
      // Handle file preview if needed
    },
    onFolderTreeLoaded: (tree: any) => {
      // Handle folder tree if needed
    },
    onFileInfoLoaded: (info: any) => {
      // Handle file info if needed
    },
  }));
  const [selectedGoogleDriveFiles, setSelectedGoogleDriveFiles] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState('myDrive');
  const [teamDrives, setTeamDrives] = useState<any[]>([]);
  const [selectedTeamDrive, setSelectedTeamDrive] = useState<string | null>(null);

  // Helper functions for Google Drive
  const getFileTypeIcon = (mimeType: string) => {
    if (mimeType?.includes('pdf')) return '📄';
    if (mimeType?.includes('word') || mimeType?.includes('document')) return '📝';
    if (mimeType?.includes('sheet') || mimeType?.includes('excel')) return '📊';
    if (mimeType?.includes('presentation') || mimeType?.includes('powerpoint')) return '📈';
    if (mimeType?.includes('image')) return '🖼️';
    if (mimeType?.includes('video')) return '🎥';
    if (mimeType?.includes('audio')) return '🎵';
    return '📄';
  };

  const getFileTypeLabel = (mimeType: string) => {
    if (mimeType?.includes('pdf')) return 'PDF';
    if (mimeType?.includes('word') || mimeType?.includes('document')) return 'Word';
    if (mimeType?.includes('sheet') || mimeType?.includes('excel')) return 'Excel';
    if (mimeType?.includes('presentation') || mimeType?.includes('powerpoint')) return 'PowerPoint';
    if (mimeType?.includes('image')) return 'Image';
    if (mimeType?.includes('video')) return 'Video';
    if (mimeType?.includes('audio')) return 'Audio';
    return 'File';
  };

  // Image Preview Component
  const ImagePreview = ({ file }: { file: any }) => {
    const [imageError, setImageError] = useState(false);
    const [imageLoading, setImageLoading] = useState(true);

    const getImageBackground = (mimeType: string) => {
      if (mimeType?.includes('image')) {
        return 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
      }
      return 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
    };

    if (imageError || !file.thumbnailLink) {
      return (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            background: getImageBackground(file.mimeType),
            color: 'white',
            borderRadius: '8px',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <Typography sx={{ fontSize: '24px', mb: 1 }}>🖼️</Typography>
          <Typography sx={{ fontSize: '10px', textAlign: 'center', px: 1 }}>
            {getFileTypeLabel(file.mimeType)}
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
        {imageLoading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: getImageBackground(file.mimeType),
              color: 'white',
              borderRadius: '8px',
            }}
          >
            <CircularProgress size={20} sx={{ color: 'white' }} />
          </Box>
        )}
        <img
          src={file.thumbnailLink}
          alt={file.name}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            borderRadius: '8px',
            display: imageLoading ? 'none' : 'block',
          }}
          onLoad={() => setImageLoading(false)}
          onError={() => {
            setImageError(true);
            setImageLoading(false);
          }}
        />
      </Box>
    );
  };

  // Tab handling functions
  const handleGoogleDriveTabChange = (newTab: string) => {
    setActiveTab(newTab);
    setSelectedTeamDrive(null);
  };

  const handleTeamDriveSelect = (teamDriveId: string) => {
    setSelectedTeamDrive(teamDriveId);
  };

  const loadFilesForActiveTab = async () => {
    if (!googleDriveAuthState.isAuthenticated || !googleDriveAuthState.sessionId) return;

    try {
      setGoogleDriveAuthState(prev => ({ ...prev, loading: true }));

      if (activeTab === 'myDrive') {
        const result = await googleDriveService.loadMyDriveFiles(googleDriveAuthState.sessionId, 50);
        setGoogleDriveFiles(result.files || []);
      } else if (activeTab === 'shared') {
        const result = await googleDriveService.loadSharedFiles(googleDriveAuthState.sessionId, 50);
        setGoogleDriveFiles(result.files || []);
      } else if (activeTab === 'teamDrives') {
        const drivesResult = await googleDriveService.loadTeamDrives(googleDriveAuthState.sessionId, 50);
        setTeamDrives(drivesResult.drives || []);
        if (selectedTeamDrive) {
          const filesResult = await googleDriveService.loadTeamDriveFiles(googleDriveAuthState.sessionId, selectedTeamDrive, 50);
          setGoogleDriveFiles(filesResult.files || []);
        }
      }
    } catch (error) {
      console.error('Error loading files:', error);
      setGoogleDriveAuthState(prev => ({ ...prev, error: 'Failed to load files' }));
    } finally {
      setGoogleDriveAuthState(prev => ({ ...prev, loading: false }));
    }
  };

  // Filter files based on search query
  const filteredGoogleDriveFiles = googleDriveFiles.filter(file =>
    file.name.toLowerCase().includes(googleDriveSearchQuery.toLowerCase())
  );

  const fetchFinancialData = async (campaignId: string) => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${environment.controlplaneUrl}/agent/flow/execute`,
        {
          data: {
            topic: '',
            keywords: [],
            pageSize: 25,
            pageNumber: 1,
            contentType: 'COMPANY_FINANCIAL_REPORT',
            campaignId: campaignId,
          },
          agent: {
            code: 'QUERY_COMPANY_RESEARCH_INTEL',
            groupBy: 'contentClass',
          },
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        }
      );
      setFinancialData(response.data.data);
      setSuccess('Financial data loaded successfully');
    } catch (err: any) {
      if (
        err?.response?.data?.error === 'jwt malformed' ||
        err?.response?.data?.error === 'jwt expired'
      ) {
        console.log(err?.response?.data?.error);
        navigate('/login');
      }
      setError('Failed to fetch financial data');
      console.error('Error fetching financial data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchProjects = async () => {
      setIsLoadingProjects(true);
      setInitialLoadingState(prev => ({ ...prev, projects: true }));
      try {
        const response = await axios.post<Project[]>(
          `${environment.apiUrl}/workspace/getProjectForOrg`,
          {
            data: {
              org_id: localStorage.getItem('org_id'),
              project_type: ['CMPN_RSRC']
            }
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json',
            },
          }
        );
        setProjects(response.data);
        if (response.data.length > 0) {
          setSelectedProject(response.data[0]);
          // fetchFinancialData(response.data[0].id);
        }
      } catch (err: any) {
        if (
          err?.response?.data?.error === 'jwt malformed' ||
          err?.response?.data?.error === 'jwt expired'
        ) {
          console.log(err?.response?.data?.error);
          navigate('/login');
        }
      } finally {
        setIsLoadingProjects(false);
        setInitialLoadingState(prev => ({ ...prev, projects: false }));
      }
    };
    fetchProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // RBAC: Fetch project permissions
  const fetchProjectPermissions = async () => {
    if (!selectedProject) return;

    const projectId = selectedProject.id;

    // Only update loading state if this is still the current project
    if (currentProjectIdRef.current !== projectId) return;

    const userId = localStorage.getItem('user_email') || 'demouser01@bahwancybertek.com';
    const organisationId = localStorage.getItem('org_id') || 'bctglobal';

    setLoadingPermissions(true);
    setInitialLoadingState(prev => ({ ...prev, permissions: true }));

    try {
      // Call the correct RBAC endpoint: /analytics/rbac/users/{userId}/project-permissions/{projectId}
      const response = await fetch(
        `${environment.apiUrl}/analytics/rbac/users/${userId}/project-permissions/${selectedProject.id}?organisationId=${organisationId}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.ok) {
        const data = await response.json();

        // Check both canView and canEdit permissions
        const canView = data.permissions?.canView || false;
        const canEdit = data.permissions?.canEdit || false;
        setCanViewProject(canView);
        setCanEditProject(canEdit);

        console.log('🔐 Project permissions loaded:', { projectId: selectedProject.id, canView, canEdit, data });
      } else {
        console.error('❌ Project permission fetch failed:', response.status);
        setCanViewProject(false);
        setCanEditProject(false);
      }
    } catch (error) {
      console.error('❌ Project permission fetch error:', error);
      // Set default to false if fetch fails
      setCanViewProject(false);
      setCanEditProject(false);
    } finally {
      // Only update loading state if this is still the current project
      if (currentProjectIdRef.current === projectId) {
        setLoadingPermissions(false);
        setInitialLoadingState(prev => ({ ...prev, permissions: false }));
      }
    }
  };

  useEffect(() => {
    if (selectedProject) {
      const projectId = selectedProject.id;
      const projectChanged = currentProjectIdRef.current !== projectId;

      // If project changed, reset all loading states and clear old data
      if (projectChanged) {
        // Clear old comprehensive data
        setComprehensiveData([]);

        // Reset all loading states to true for new project BEFORE updating ref
        setInitialLoadingState({
          projects: false, // Projects already loaded
          comprehensiveData: true,
          permissions: true,
          contentTypes: false // Content types don't depend on project
        });

        // Update the ref to track current project
        currentProjectIdRef.current = projectId;
      }

      // fetchFinancialData(selectedProject.id);
      fetchComprehensiveData(selectedProject.id);
    } else {
      // No project selected, reset ref
      currentProjectIdRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProject]);

  // RBAC: Fetch project permissions when project changes
  useEffect(() => {
    if (selectedProject) {
      const projectId = selectedProject.id;

      // Always fetch permissions when project is selected
      // The fetchProjectPermissions function will check if it's still the current project
      fetchProjectPermissions();
    }
  }, [selectedProject]);

  // Google Drive authentication effect
  useEffect(() => {
    const initializeGoogleDrive = async () => {
      try {
        const sessionId = localStorage.getItem('googleDriveSessionId');
        if (sessionId) {
          const isAuthenticated = await googleDriveService.checkAuthStatus(sessionId);
          if (isAuthenticated) {
            setGoogleDriveAuthState({
              isAuthenticated: true,
              sessionId,
              loading: false,
              error: null,
            });
          } else {
            localStorage.removeItem('googleDriveSessionId');
          }
        }
      } catch (error) {
        console.error('Error checking Google Drive auth status:', error);
        localStorage.removeItem('googleDriveSessionId');
      }
    };

    initializeGoogleDrive();
  }, [googleDriveService]);

  // Load files when dialog opens or tab changes
  useEffect(() => {
    if (showGoogleDriveDialog && googleDriveAuthState.isAuthenticated && googleDriveAuthState.sessionId) {
      loadFilesForActiveTab();
    }
  }, [showGoogleDriveDialog, activeTab, googleDriveAuthState.isAuthenticated, googleDriveAuthState.sessionId, selectedTeamDrive]);

  // Set first available category as default when data loads
  useEffect(() => {
    if (comprehensiveData.length > 0 && !selectedCategory) {
      const availableCategories = INFORMATION_CATEGORIES
        .filter((category) => {
          // Remove Dashboard and Others from sidebar
          if (category.informationCategory === 'DASHBOARD' || category.informationCategory === 'OTHER') {
            return false;
          }

          const categoryData = comprehensiveData.find(d => d.category === category.informationCategory);
          const hasData = categoryData?.data && categoryData.data.length > 0;

          return hasData;
        });

      if (availableCategories.length > 0) {
        setSelectedCategory(availableCategories[0].informationCategory);
      }
    }
  }, [comprehensiveData, selectedCategory]);

  // Fetch content types on component mount
  useEffect(() => {
    fetchContentTypes();
  }, []);

  const callCustomAPI = async (userMessage: string, docId: string) => {
    try {
      const payload = {
        data: {
          query: userMessage,
          campaignId: selectedProject?.id || ""
        },
        agent: {
          code: "QUERY_RESEARCH_INTEL_AND_RESPOND"
        }
      };

      const response = await axios.post(
        `${environment.controlplaneUrl}/agent/flow/execute`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data?.data) {
        const responseData = response.data.data;
        const paragraphResponse = responseData.find((item: any) => item.objectType === 'PARAGRAPH');
        const followupQuestions = responseData.find((item: any) => item.objectType === 'MULTITEXTOPTIONS' && item.key === 'followupQuestions');
        const sourceLinks = responseData.find((item: any) => item.objectType === 'MULTITEXTOPTIONS' && item.key === 'sourceLinks');
        const dictionaryResponse = responseData.find((item: any) => item.objectType === 'DICTIONARY');
        const multiColumnResponse = responseData.find((item: any) => item.objectType === 'MULTICOLUMN');

        // Handle MULTICOLUMN visualization data with improved processing
        let visualization = undefined;

        // Check for MULTICOLUMN data in DICTIONARY structure first
        if (dictionaryResponse?.entries) {
          const datasetEntry = dictionaryResponse.entries.find((entry: any) => entry.key === 'dataset');
          if (datasetEntry?.value) {
            const data = datasetEntry.value;
            // Process and clean the data before creating visualization
            const processedData = processMultiColumnData(data, data.visual);
            if (processedData) {
              let type: 'table' | 'chart' | 'heatmap' = 'table';
              if (processedData.visual) {
                if (processedData.dimension === 2) {
                  type = 'chart';
                } else if (processedData.dimension === 3 || processedData.dimension === 4) {
                  type = 'heatmap';
                }
              }
              visualization = {
                type,
                data: processedData
              };
            }
          }
        }

        // Check for direct MULTICOLUMN structure if not found in DICTIONARY
        if (!visualization && multiColumnResponse?.value) {
          const data = multiColumnResponse.value;
          // Process and clean the data before creating visualization
          const processedData = processMultiColumnData(data, data.visual);
          if (processedData) {
            let type: 'table' | 'chart' | 'heatmap' = 'table';
            if (processedData.visual) {
              if (processedData.dimension === 2) {
                type = 'chart';
              } else if (processedData.dimension === 3 || processedData.dimension === 4) {
                type = 'heatmap';
              }
            }
            visualization = {
              type,
              data: processedData
            };
          }
        }

        // Extract interaction UID from the response
        let interactionUID = undefined;

        console.log('callCustomAPI - responseData:', responseData);
        console.log('callCustomAPI - dictionaryResponse:', dictionaryResponse);

        // First try to find it in DICTIONARY entries
        if (dictionaryResponse?.entries) {
          const uidEntry = dictionaryResponse.entries.find((entry: any) => entry.key === 'interactionUID');
          console.log('callCustomAPI - uidEntry from DICTIONARY:', uidEntry);
          if (uidEntry) {
            interactionUID = uidEntry.value;
          }
        }

        // If not found in DICTIONARY, look for IDENTIFIER object type
        if (!interactionUID) {
          const identifierResponse = responseData.find((item: any) => item.objectType === 'IDENTIFIER');
          console.log('callCustomAPI - identifierResponse:', identifierResponse);
          if (identifierResponse && identifierResponse.key === 'interactionId') {
            interactionUID = identifierResponse.value;
            console.log('callCustomAPI - found interactionUID from IDENTIFIER:', interactionUID);
          }
        }

        console.log('callCustomAPI - final interactionUID:', interactionUID);

        return {
          response: paragraphResponse?.value || 'No response available.',
          followupQuestions: followupQuestions?.value || [],
          sourceLinks: sourceLinks?.value || [],
          visualization,
          interactionUID
        };
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      console.error('Error calling custom API:', error);
      throw error;
    }
  };

  // Old chat functions removed - now using reusable Chatbot component
  // The Chatbot component handles all messaging internally

  const handlePageChange = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleProjectChange = (projectId: string) => {
    const proj = projects.find((p) => p.id === projectId);
    setSelectedProject(proj || null);
  };

  const handleChatClick = () => {
    setIsChatOpen(true);
  };

  const handleCloseChat = () => {
    setIsChatOpen(false);
  };

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadLoading(true);
    setUploadError(null);

    try {
      // First upload the file
      const formData = new FormData();
      formData.append('file', file);
      formData.append('token', `Bearer ${localStorage.getItem('token')}`);
      formData.append('type', 'docs');
      formData.append('campaignId', selectedProject?.id || '');
      const uploadResponse = await axios.post(
        `${environment.apiUrl}/account/uploadFileForOrg`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (uploadResponse.data.status === 'success') {
        // Then execute the flow with the uploaded file
        await axios.post(
          `${environment.controlplaneUrl}/agent/flow/execute`,
          {
            data: {
              filePath: file.name,
              sourceName: 'Company Website',
              fileType: 'docs',
              topic: selectedProject?.project_name,
              keywords: [
                'Company Earnings',
                'Financial Performance',
                'Share Price',
                'Sector Performance',
                'Initial Public Offering (IPO)',
                'Market Share',
                'Market Capitalization',
                'Stock Analysis',
                'Investment Fund Raise',
                'Contract Award',
                'Share Buyback',
                'Stock Split',
                'Demerger',
                'Dividend',
                'Bonus Issue',
                'Rights Issue',
                'Merger',
                'Acquisition',
                'New Product Launch',
                'Partnership Announcement',
                'Capacity Augmentation',
              ],
              contentType: 'COMPANY_FINANCIAL_REPORT',
              campaignId: selectedProject?.id,
            },
            agent: {
              code: 'INDEX_COMPANY_RESEARCH_DOCUMENT',
            },
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json',
            },
          }
        );

        // Refresh the data
        await fetchFinancialData(selectedProject?.id || '');

        setUploadDialogOpen(false);
        setSuccess('Document uploaded successfully');
      } else {
        throw new Error('Upload failed');
      }
    } catch (error: any) {
      console.error('Error uploading document:', error);
      if (
        error?.response?.data?.error === 'jwt malformed' ||
        error?.response?.data?.error === 'jwt expired'
      ) {
        console.log(error?.response?.data?.error);
        navigate('/login');
      }
      setUploadError('Failed to upload document. Please try again.');
    } finally {
      setUploadLoading(false);
    }
  };

  // Google Drive authentication functions
  const handleGoogleDriveAuth = async () => {
    try {
      setGoogleDriveAuthState(prev => ({ ...prev, loading: true, error: null }));

      const authResult = await googleDriveService.initiateAuth();
      if (authResult.sessionId) {
        localStorage.setItem('googleDriveSessionId', authResult.sessionId);

        // Open auth window
        const authWindow = window.open(authResult.authUrl, 'googleDriveAuth', 'width=500,height=600,scrollbars=yes,resizable=yes');

        if (authWindow) {
          // Poll for authentication completion
          const pollAuth = setInterval(async () => {
            try {
              if (authWindow.closed) {
                clearInterval(pollAuth);
                setGoogleDriveAuthState(prev => ({ ...prev, loading: false }));
                return;
              }

              const isAuthenticated = await googleDriveService.checkAuthStatus(authResult.sessionId);
              if (isAuthenticated) {
                clearInterval(pollAuth);
                authWindow.close();
                setGoogleDriveAuthState({
                  isAuthenticated: true,
                  sessionId: authResult.sessionId,
                  loading: false,
                  error: null,
                });
                setShowGoogleDriveDialog(true);
              }
            } catch (error) {
              console.error('Auth polling error:', error);
            }
          }, 2000);
        }
      }
    } catch (error) {
      console.error('Google Drive auth error:', error);
      setGoogleDriveAuthState(prev => ({
        ...prev,
        loading: false,
        error: 'Failed to authenticate with Google Drive'
      }));
    }
  };

  const handleGoogleDriveFileSelect = (file: any) => {
    setSelectedGoogleDriveFiles([file]);
  };

  const handleGoogleDriveUpload = async () => {
    if (selectedGoogleDriveFiles.length === 0) return;

    setUploadLoading(true);
    setUploadError(null);

    try {
      for (const file of selectedGoogleDriveFiles) {
        // Download file from Google Drive
        const downloadUrl = await googleDriveService.getDownloadUrl(googleDriveAuthState.sessionId, file.id);

        // Create a blob from the downloaded file
        const response = await fetch(downloadUrl);
        const blob = await response.blob();

        // Create FormData for upload
        const formData = new FormData();
        formData.append('file', blob, file.name);
        formData.append('token', `Bearer ${localStorage.getItem('token')}`);
        formData.append('type', 'docs');

        const uploadResponse = await axios.post(
          `${environment.apiUrl}/account/uploadFileForOrg`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          }
        );

        if (uploadResponse.data.status === 'success') {
          // Execute the flow with the uploaded file
          await axios.post(
            `${environment.controlplaneUrl}/agent/flow/execute`,
            {
              data: {
                filePath: file.name,
                sourceName: 'Google Drive',
                fileType: 'docs',
                topic: selectedProject?.project_name,
                keywords: [
                  'Company Earnings',
                  'Financial Performance',
                  'Share Price',
                  'Sector Performance',
                  'Initial Public Offering (IPO)',
                  'Market Share',
                  'Market Capitalization',
                  'Stock Analysis',
                  'Investment Fund Raise',
                  'Contract Award',
                  'Share Buyback',
                  'Stock Split',
                  'Demerger',
                  'Dividend',
                  'Bonus Issue',
                  'Rights Issue',
                  'Merger',
                  'Acquisition',
                  'New Product Launch',
                  'Partnership Announcement',
                  'Capacity Augmentation',
                ],
                contentType: 'COMPANY_FINANCIAL_REPORT',
                campaignId: selectedProject?.id,
              },
              agent: {
                code: 'INDEX_COMPANY_RESEARCH_DOCUMENT',
              },
            },
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem('token')}`,
                'Content-Type': 'application/json',
              },
            }
          );
        }
      }

      // Refresh the data
      await fetchComprehensiveData(selectedProject?.id || '');

      setShowGoogleDriveDialog(false);
      setSelectedGoogleDriveFiles([]);
      setSuccess('Document(s) uploaded successfully from Google Drive');
    } catch (error: any) {
      console.error('Error uploading from Google Drive:', error);
      if (
        error?.response?.data?.error === 'jwt malformed' ||
        error?.response?.data?.error === 'jwt expired'
      ) {
        console.log(error?.response?.data?.error);
        navigate('/login');
      }
      setUploadError('Failed to upload document from Google Drive. Please try again.');
    } finally {
      setUploadLoading(false);
    }
  };

  const handleCreateProject = async () => {
    if (!newProject.project_name.trim()) {
      setProjectError('Project name is required');
      return;
    }

    setIsSubmittingProject(true);
    setProjectError('');

    try {
      const response = await axios.post(
        `${environment.apiUrl}/workspace/updateCustomProjectForOrg`,
        {
          data: {
            org_id: localStorage.getItem("org_id"),
            source: "10_org",
            project_items: [
              {
                project_name: newProject.project_name,
                project_description: newProject.project_description,
                project_type: 'CMPN_RSRC',
                configuration: {
                  period_start_date: newProject.period_start_date,
                  period_end_date: newProject.period_end_date,
                  period_type: newProject.period_type || 'D'
                }
              }
            ]
          }
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data.upsertRef && response.data.upsertRef.length > 0) {
        // Fetch project details
        const projectDetails = await axios.post(
          `${environment.apiUrl}/workspace/getProjectDetails`,
          {
            data: {
              id: response.data.upsertRef[0]
            }
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json',
            },
          }
        );

        // Add the new project to the list
        setProjects(prev => [...prev, projectDetails.data]);

        // Select the new project
        setSelectedProject(projectDetails.data);

        // Reset form
        setNewProject({
          project_name: '',
          project_description: '',
          project_type: 'CMPN_RSRC',
          period_start_date: new Date().toISOString().split('T')[0],
          period_end_date: new Date().toISOString().split('T')[0],
          period_type: 'daily',
          source: 'manual',
          active: true,
          status: 'active'
        });

        setIsProjectDialogOpen(false);
        setSuccess('Project created successfully');

        // Auto-assign permissions for the new project
        try {
          const autoAssignResponse = await axios.post(
            `${environment.apiUrl}/manage/project-permissions/auto-assign`,
            {
              projectId: projectDetails.data.id,
              userId: localStorage.getItem('user_email') || 'admin@example.com',
              organisationId: localStorage.getItem('org_id') || 'org123'
            },
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem('token')}`,
                'Content-Type': 'application/json',
              },
            }
          );

          console.log('✅ Auto-assign permissions successful:', autoAssignResponse.data);

          // Refresh project permissions after auto-assign
          await fetchProjectPermissions();

        } catch (autoAssignError: any) {
          console.error('⚠️ Auto-assign permissions failed:', autoAssignError);
          // Don't show error to user as project creation was successful
          // Auto-assign is a convenience feature, not critical
        }
      } else {
        setProjectError('Failed to create project');
      }
    } catch (error: any) {
      console.error('Error creating project:', error);
      if (
        error?.response?.data?.error === 'jwt malformed' ||
        error?.response?.data?.error === 'jwt expired'
      ) {
        console.log(error?.response?.data?.error);
        navigate('/login');
      }
      setProjectError(error?.response?.data?.message || 'Failed to create project. Please try again.');
    } finally {
      setIsSubmittingProject(false);
    }
  };

  // Export functionality handlers - copied from SurveyInsights.tsx
  const audienceOptions = [
    'General Public',
    'Business Professionals',
    'Corporate clients',
    'Small business owners',
    'Internal employees',
    'Investment clients',
    'Insurance customers',
    'Loan applicants',
    'Credit card users'
  ];

  const handleExportMode = () => {
    setIsExportMode(true);
    setSelectedPairs(new Set());
  };

  const handleCancelExport = () => {
    setIsExportMode(false);
    setSelectedPairs(new Set());
  };

  const handlePairSelection = (pairId: string) => {
    const newSelected = new Set(selectedPairs);
    if (newSelected.has(pairId)) {
      newSelected.delete(pairId);
    } else {
      newSelected.add(pairId);
    }
    setSelectedPairs(newSelected);
  };

  const handleExport = () => {
    if (selectedPairs.size === 0) return;

    setIsExportMode(false);
    setShowExportDialog(true);
    setCurrentStep(1);
  };

  const handleCloseExportDialog = () => {
    setShowExportDialog(false);
    setSelectedPairs(new Set());
    setCurrentStep(1);
  };

  const handleNextStep = () => {
    if (currentStep < 2) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleBrandDetailsChange = (field: keyof BrandDetails, value: string) => {
    setBrandDetails(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAudienceToggle = (audience: string) => {
    setBrandDetails(prev => ({
      ...prev,
      targetAudience: prev.targetAudience.includes(audience)
        ? prev.targetAudience.filter(a => a !== audience)
        : [...prev.targetAudience, audience]
    }));
  };

  const handleAddCustomAudience = () => {
    if (newAudience.trim() && !brandDetails.targetAudience.includes(newAudience.trim())) {
      setBrandDetails(prev => ({
        ...prev,
        targetAudience: [...prev.targetAudience, newAudience.trim()]
      }));
      setNewAudience('');
    }
  };

  const handleRemoveAudience = (audience: string) => {
    setBrandDetails(prev => ({
      ...prev,
      targetAudience: prev.targetAudience.filter(a => a !== audience)
    }));
  };

  const handleContentPreferenceChange = (field: keyof ContentPreferences, value: string) => {
    setContentPreferences(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const isContentLengthApplicable = () => {
    const textBasedFormats = ['Text Only'];
    return textBasedFormats.includes(contentPreferences.format);
  };

  // Helper function to map content preferences to API format
  const mapContentPreferencesToAPI = () => {
    // Content types are now fetched from API and already in correct format
    const contentTypeMap: { [key: string]: string } = {
      'SOCIAL_POST_INSTAGRAM': 'SOCIAL_POST_INSTAGRAM',
      'SOCIAL_POST_FACEBOOK': 'SOCIAL_POST_FACEBOOK',
      'SOCIAL_POST_LINKEDIN': 'SOCIAL_POST_LINKEDIN',
      'SOCIAL_POST_TWITTER': 'SOCIAL_POST_TWITTER',
      'BLOG_POST': 'BLOG_POST',
      'EMAIL': 'EMAIL',
      'VIDEO': 'VIDEO',
      'SHORT_MESSAGE': 'SHORT_MESSAGE',
      'JOB_DESCRIPTION': 'JOB_DESCRIPTION',
      'CUSTOM': 'CUSTOM'
    };

    const writingStyleMap: { [key: string]: string } = {
      'Professional': 'professional',
      'Casual': 'casual',
      'Technical': 'technical',
      'Creative': 'creative'
    };

    return {
      contentType: contentTypeMap[contentPreferences.category] || 'SOCIAL_POST_LINKEDIN',
      writingStyle: writingStyleMap[contentPreferences.style] || 'professional',
      tone: 'warm', // Default tone
      audience: brandDetails.targetAudience.length > 0 ? brandDetails.targetAudience[0] : 'general',
      guidelines: contentPreferences.instructions || ''
    };
  };

  // Helper function to extract interaction UIDs from selected pairs
  // Export functionality is not available with reusable chatbot as it manages messages internally
  const getInteractionUIDs = (): string[] => {
    console.warn('Export functionality is not available with the reusable chatbot');
    return [];
  };

  const formatMessageContent = (message: ChatMessage) => {
    try {
      // If the message is a plain string and not JSON, return it with markdown formatting
      if (typeof message.text === 'string' && !message.text.startsWith('{')) {
        return <MarkdownMessage text={message.text} />;
      }

      // Parse the message if it's a string, or use it directly if it's already an object
      const data = typeof message.text === 'string' ? JSON.parse(message.text) : message.text;

      // If data is an array, wrap it in an object with a data property
      const formattedData = Array.isArray(data) ? { data } : data;

      if (formattedData.data) {
        return (
          <Box sx={{ width: '100%' }}>
            {formattedData.data.map((item: any, index: number) => {
              if (item.objectType === 'DICTIONARY') {
                const entries = item.entries || [];

                // Map all entries with their values
                const mappedEntries = entries.reduce((acc: Record<string, any>, entry: any) => {
                  acc[entry.key] = entry.value;
                  return acc;
                }, {});

                // Extract specific fields for financial data
                const title = mappedEntries.title || '';
                const description = mappedEntries.description || '';
                const highlights = mappedEntries.highlights || [];
                const riskRating = mappedEntries.riskRating || '';
                const financialMetrics = mappedEntries.financialMetrics || {};
                const recommendations = mappedEntries.recommendations || [];

                return (
                  <Box key={index} sx={{ mb: 2 }}>
                    {title && (
                      <Typography variant="subtitle1" sx={{
                        fontWeight: 600,
                        color: '#131F32',
                        mb: 1
                      }}>
                        {title}
                      </Typography>
                    )}

                    {description && (
                      <Typography variant="body2" sx={{
                        color: '#64748B',
                        mb: 2,
                        lineHeight: 1.6
                      }}>
                        <MarkdownMessage text={description} />
                      </Typography>
                    )}

                    {highlights && highlights.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{
                          fontWeight: 600,
                          color: '#131F32',
                          mb: 1
                        }}>
                          Key Highlights
                        </Typography>
                        <Box sx={{ pl: 2 }}>
                          {highlights.map((highlight: string, idx: number) => (
                            <Typography key={idx} variant="body2" sx={{
                              color: '#64748B',
                              mb: 0.5,
                              '&:before': {
                                content: '"• "',
                                color: '#4c3794',
                                fontWeight: 'bold'
                              }
                            }}>
                              {highlight}
                            </Typography>
                          ))}
                        </Box>
                      </Box>
                    )}

                    {riskRating && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{
                          fontWeight: 600,
                          color: '#131F32',
                          mb: 1
                        }}>
                          Risk Assessment
                        </Typography>
                        <Chip
                          label={riskRating}
                          size="small"
                          sx={{
                            backgroundColor: riskRating.includes('High') ? '#FBE9E7' :
                              riskRating.includes('Medium') ? '#FFF3E0' : '#E8F5E9',
                            color: riskRating.includes('High') ? '#D32F2F' :
                              riskRating.includes('Medium') ? '#F57C00' : '#2E7D32',
                            fontWeight: 600
                          }}
                        />
                      </Box>
                    )}

                    {Object.keys(financialMetrics).length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{
                          fontWeight: 600,
                          color: '#131F32',
                          mb: 1
                        }}>
                          Financial Metrics
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {Object.entries(financialMetrics).map(([key, value]) => (
                            <Chip
                              key={key}
                              label={`${key}: ${value}`}
                              size="small"
                              sx={{
                                backgroundColor: 'rgba(76, 55, 148, 0.1)',
                                color: '#4c3794',
                                fontSize: '0.75rem'
                              }}
                            />
                          ))}
                        </Box>
                      </Box>
                    )}

                    {recommendations && recommendations.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{
                          fontWeight: 600,
                          color: '#131F32',
                          mb: 1
                        }}>
                          Recommendations
                        </Typography>
                        <Box sx={{ pl: 2 }}>
                          {recommendations.map((rec: string, idx: number) => (
                            <Typography key={idx} variant="body2" sx={{
                              color: '#64748B',
                              mb: 0.5,
                              '&:before': {
                                content: '"→ "',
                                color: '#4c3794',
                                fontWeight: 'bold'
                              }
                            }}>
                              {rec}
                            </Typography>
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Box>
                );
              } else if (item.objectType === 'PARAGRAPH') {
                return (
                  <Typography key={index} variant="body2" sx={{
                    whiteSpace: 'pre-wrap',
                    mb: 2,
                    color: '#666',
                    lineHeight: 1.6
                  }}>
                    <MarkdownMessage text={item.value} />
                  </Typography>
                );
              } else if (item.objectType === 'MULTICOLUMN') {
                // Handle MULTICOLUMN data for financial tables/charts
                const data = item.value;
                if (data && data.headers && data.data) {
                  // Check if visualization is requested
                  if (data.visual) {
                    // Show chart based on dimension
                    if (data.dimension === 2 || data.dimension === 3) {
                      // Bar chart for 2D data, Line chart for 3D data
                      const chartData = data.data.map((row: any) => {
                        const chartItem: any = {};
                        data.headers.forEach((header: string) => {
                          chartItem[header] = row[header];
                        });
                        return chartItem;
                      });

                      return (
                        <Box key={index} sx={{
                          mt: 2,
                          borderRadius: 2,

                          border: '1px solid rgba(76, 175, 80, 0.1)',
                          backgroundColor: '#fff'
                        }}>
                          <Typography variant="h6" sx={{
                            fontWeight: 600,
                            color: '#4c3794',
                            mb: 2,
                            fontSize: '1rem'
                          }}>
                            {data.title || 'Financial Data'}
                          </Typography>
                          <ResponsiveContainer width="100%" height={250}>
                            {data.dimension === 2 ? (
                              <BarChart data={chartData} margin={{ top: 20, right: 30, left: -10, bottom: 10 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                <XAxis
                                  dataKey={data.headers[0]}
                                  angle={-45}
                                  textAnchor="end"
                                  height={70}
                                  tick={{ fontSize: 12, fill: '#4c3794' }}
                                  tickLine={{ stroke: '#81C784' }}
                                />
                                <YAxis
                                  tick={{ fontSize: 12, fill: '#4c3794' }}
                                  tickLine={{ stroke: '#81C784' }}
                                />
                                <RechartsTooltip />
                                <Bar
                                  dataKey={data.headers[1]}
                                  radius={[4, 4, 0, 0]}
                                  maxBarSize={50}
                                  fill="#4c3794"
                                />
                              </BarChart>
                            ) : (
                              <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                <XAxis
                                  dataKey={data.headers[1]}
                                  angle={-45}
                                  textAnchor="end"
                                  height={70}
                                  tick={{ fontSize: 12, fill: '#4c3794' }}
                                  tickLine={{ stroke: '#81C784' }}
                                />
                                <YAxis
                                  tick={{ fontSize: 12, fill: '#4c3794' }}
                                  tickLine={{ stroke: '#81C784' }}
                                />
                                <RechartsTooltip />
                                <Legend />
                                <Line
                                  type="monotone"
                                  dataKey={data.headers[2]}
                                  stroke="#4c3794"
                                  strokeWidth={2}
                                  dot={{
                                    r: 4,
                                    fill: '#4c3794',
                                    strokeWidth: 2,
                                    stroke: '#fff'
                                  }}
                                  activeDot={{
                                    r: 6,
                                    fill: '#4c3794',
                                    stroke: '#fff',
                                    strokeWidth: 2
                                  }}
                                />
                              </LineChart>
                            )}
                          </ResponsiveContainer>
                        </Box>
                      );
                    } else {
                      // For other dimensions, show table with visual indicator
                      return (
                        <Box key={index} sx={{
                          mt: 2,
                          borderRadius: 2,
                          overflow: 'hidden',
                          border: '1px solid rgba(76, 175, 80, 0.1)',
                          backgroundColor: '#fff',
                          maxHeight: '300px',
                          overflowY: 'auto'
                        }}>
                          <Box sx={{
                            p: 2,
                            backgroundColor: 'rgba(76, 175, 80, 0.05)',
                            borderBottom: '1px solid rgba(76, 175, 80, 0.1)'
                          }}>
                            <Typography variant="h6" sx={{
                              fontWeight: 600,
                              color: '#4c3794',
                              fontSize: '1rem'
                            }}>
                              {data.title || 'Financial Data'} (Visual Data)
                            </Typography>
                          </Box>
                          <Box sx={{ overflowX: 'auto' }}>
                            <table style={{
                              width: '100%',
                              borderCollapse: 'collapse',
                              fontSize: '0.875rem'
                            }}>
                              <thead>
                                <tr style={{ backgroundColor: 'rgba(76, 175, 80, 0.05)' }}>
                                  {data.headers.map((header: string, headerIndex: number) => (
                                    <th key={headerIndex} style={{
                                      padding: '12px 16px',
                                      textAlign: 'left',
                                      fontWeight: 600,
                                      color: '#4c3794',
                                      borderBottom: '1px solid rgba(76, 175, 80, 0.1)',
                                      fontSize: '0.875rem'
                                    }}>
                                      {header}
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {data.data.map((row: any, rowIndex: number) => (
                                  <tr key={rowIndex} style={{
                                    backgroundColor: rowIndex % 2 === 0 ? '#fff' : 'rgba(76, 175, 80, 0.02)'
                                  }}>
                                    {data.headers.map((header: string, colIndex: number) => (
                                      <td key={colIndex} style={{
                                        padding: '12px 16px',
                                        borderBottom: '1px solid rgba(76, 175, 80, 0.05)',
                                        color: '#333',
                                        fontSize: '0.875rem'
                                      }}>
                                        {row[header]}
                                      </td>
                                    ))}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </Box>
                        </Box>
                      );
                    }
                  } else {
                    // Show regular table when visual is false
                    return (
                      <Box key={index} sx={{
                        mt: 2,
                        borderRadius: 2,
                        overflow: 'hidden',
                        border: '1px solid rgba(76, 175, 80, 0.1)',
                        backgroundColor: '#fff',
                        maxHeight: '300px',
                        overflowY: 'auto'
                      }}>
                        <Box sx={{
                          p: 2,
                          backgroundColor: 'rgba(76, 175, 80, 0.05)',
                          borderBottom: '1px solid rgba(76, 175, 80, 0.1)'
                        }}>
                          <Typography variant="h6" sx={{
                            fontWeight: 600,
                            color: '#4c3794',
                            fontSize: '1rem'
                          }}>
                            {data.title || 'Financial Data'}
                          </Typography>
                        </Box>
                        <Box sx={{ overflowX: 'auto' }}>
                          <table style={{
                            width: '100%',
                            borderCollapse: 'collapse',
                            fontSize: '0.875rem'
                          }}>
                            <thead>
                              <tr style={{ backgroundColor: 'rgba(76, 175, 80, 0.05)' }}>
                                {data.headers.map((header: string, headerIndex: number) => (
                                  <th key={headerIndex} style={{
                                    padding: '12px 16px',
                                    textAlign: 'left',
                                    fontWeight: 600,
                                    color: '#4c3794',
                                    borderBottom: '1px solid rgba(76, 175, 80, 0.1)',
                                    fontSize: '0.875rem'
                                  }}>
                                    {header}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {data.data.map((row: any, rowIndex: number) => (
                                <tr key={rowIndex} style={{
                                  backgroundColor: rowIndex % 2 === 0 ? '#fff' : 'rgba(76, 175, 80, 0.02)'
                                }}>
                                  {data.headers.map((header: string, colIndex: number) => (
                                    <td key={colIndex} style={{
                                      padding: '12px 16px',
                                      borderBottom: '1px solid rgba(76, 175, 80, 0.05)',
                                      color: '#333',
                                      fontSize: '0.875rem'
                                    }}>
                                      {row[header]}
                                    </td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </Box>
                      </Box>
                    );
                  }
                }
              }
              return null;
            })}
          </Box>
        );
      }
      return typeof message.text === 'string' ? <MarkdownMessage text={message.text} /> : JSON.stringify(message.text);
    } catch (error) {
      return typeof message.text === 'string' ? <MarkdownMessage text={message.text} /> : JSON.stringify(message.text);
    }
  };

  const fetchComprehensiveData = async (campaignId: string) => {
    // Only update loading state if this is still the current project
    if (currentProjectIdRef.current === campaignId) {
      setIsLoadingComprehensiveData(true);
      setInitialLoadingState(prev => ({ ...prev, comprehensiveData: true }));
    }
    const categoryData: CategoryData[] = [];

    // Initialize loading state for all categories
    const categoryLoadingState: { [key: string]: boolean } = {};
    INFORMATION_CATEGORIES.forEach(category => {
      categoryLoadingState[category.informationCategory] = true;
    });
    setDataFetchProgress(categoryLoadingState);

    try {
      // Fetch data for each information category individually
      const promises = INFORMATION_CATEGORIES.map(async (category) => {
        try {
          const response = await axios.post(
            `${environment.controlplaneUrl}/agent/flow/execute`,
            {
              data: {
                informationCategory: category.informationCategory,
                topic: "",
                keywords: [],
                pageSize: 10,
                pageNumber: 1,
                contentType: "COMPANY_FINANCIAL_REPORT",
                campaignId: campaignId
              },
              agent: {
                code: "QUERY_COMPANY_RESEARCH_CATEGORY",
                ...(category.informationCategory === 'OTHER' && { groupBy: "contentClass" })
              }
            },
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem('token')}`,
                'Content-Type': 'application/json',
              },
            }
          );

          const result = {
            category: category.informationCategory,
            data: response.data.data || [],
            loading: false,
            error: null
          };

          // Update individual category data immediately
          setComprehensiveData(prev => {
            const existing = prev.find(item => item.category === category.informationCategory);
            if (existing) {
              return prev.map(item =>
                item.category === category.informationCategory ? result : item
              );
            } else {
              return [...prev, result];
            }
          });

          // Mark this category as loaded
          setDataFetchProgress(prev => ({
            ...prev,
            [category.informationCategory]: false
          }));

          return result;
        } catch (error: any) {
          console.error(`Error fetching ${category.informationCategory}:`, error);
          const result = {
            category: category.informationCategory,
            data: [],
            loading: false,
            error: error?.response?.data?.message || `Failed to fetch ${category.informationCategory}`
          };

          // Update individual category data immediately even on error
          setComprehensiveData(prev => {
            const existing = prev.find(item => item.category === category.informationCategory);
            if (existing) {
              return prev.map(item =>
                item.category === category.informationCategory ? result : item
              );
            } else {
              return [...prev, result];
            }
          });

          // Mark this category as loaded (even with error)
          setDataFetchProgress(prev => ({
            ...prev,
            [category.informationCategory]: false
          }));

          return result;
        }
      });

      // Wait for all promises to complete (for error handling)
      await Promise.all(promises);
      // setSuccess('Comprehensive financial data loaded successfully');
    } catch (error: any) {
      console.error('Error fetching comprehensive data:', error);
      setError('Failed to fetch comprehensive financial data');
    } finally {
      // Only update loading state if this is still the current project
      if (currentProjectIdRef.current === campaignId) {
        setIsLoadingComprehensiveData(false);
        setInitialLoadingState(prev => ({ ...prev, comprehensiveData: false }));
      }
    }
  };

  const generateContentFromAPI = async (): Promise<GeneratedContent> => {
    try {
      const interactionUIDs = getInteractionUIDs();
      const apiConfig = mapContentPreferencesToAPI();

      const payload = {
        data: {
          interactionUIDs: interactionUIDs,
          instructions: {
            keywords: brandDetails.industryKeywords.split(',').map(k => k.trim()).filter(k => k),
            writing_style: apiConfig.writingStyle,
            tone: apiConfig.tone,
            audience: apiConfig.audience,
            guidelines: apiConfig.guidelines
          },
          contentType: apiConfig.contentType
        },
        agent: {
          code: "GENERATE_CONTENT_FROM_CONVERSATION"
        }
      };

      const response = await axios.post(
        `${environment.controlplaneUrl}/agent/flow/execute`,
        payload,
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.data.status === 200 && response.data.data && response.data.data.length > 0) {
        const responseData = response.data.data;
        const paragraphResponse = responseData.find((item: any) => item.objectType === 'PARAGRAPH');
        const followupQuestions = responseData.find((item: any) => item.objectType === 'MULTITEXTOPTIONS' && item.key === 'followupQuestions');
        const sourceLinks = responseData.find((item: any) => item.objectType === 'MULTITEXTOPTIONS' && item.key === 'sourceLinks');
        const multiColumnResponse = responseData.find((item: any) => item.objectType === 'MULTICOLUMN');

        // Find all DICTIONARY objects
        const dictionaryResponses = responseData.filter((item: any) => item.objectType === 'DICTIONARY');

        // Extract text content from PARAGRAPH or DICTIONARY with text key
        let textContent = '';
        if (paragraphResponse?.value) {
          textContent = paragraphResponse.value;
        } else {
          // Look through all DICTIONARY objects for text content
          for (const dict of dictionaryResponses) {
            if (dict.entries) {
              const textEntry = dict.entries.find((entry: any) => entry.key === 'text');
              if (textEntry) {
                textContent = textEntry.value;
                break;
              }
            }
          }
        }

        // Handle MULTICOLUMN visualization data with improved processing
        let visualization = undefined;

        // Check for MULTICOLUMN data in DICTIONARY structure first
        for (const dict of dictionaryResponses) {
          if (dict.entries) {
            const datasetEntry = dict.entries.find((entry: any) => entry.key === 'dataset');
            if (datasetEntry?.value) {
              const data = datasetEntry.value;
              // Process and clean the data before creating visualization
              const processedData = processMultiColumnData(data, data.visual);
              if (processedData) {
                let type: 'table' | 'chart' | 'heatmap' = 'table';
                if (processedData.visual) {
                  if (processedData.dimension === 2) {
                    type = 'chart';
                  } else if (processedData.dimension === 3 || processedData.dimension === 4) {
                    type = 'heatmap';
                  }
                }
                visualization = {
                  type,
                  data: processedData
                };
                break; // Found visualization data, no need to continue
              }
            }
          }
        }

        // Check for standalone MULTICOLUMN data
        if (!visualization && multiColumnResponse) {
          const processedData = processMultiColumnData(multiColumnResponse, multiColumnResponse.visual);
          if (processedData) {
            let type: 'table' | 'chart' | 'heatmap' = 'table';
            if (processedData.visual) {
              if (processedData.dimension === 2) {
                type = 'chart';
              } else if (processedData.dimension === 3 || processedData.dimension === 4) {
                type = 'heatmap';
              }
            }
            visualization = {
              type,
              data: processedData
            };
          }
        }

        return {
          title: `${contentPreferences.category} - Generated Content`,
          content: textContent || 'Content generated successfully, but no text content was returned.',
          format: contentPreferences.format,
          category: contentPreferences.category,
          followupQuestions: followupQuestions?.value || [],
          sourceLinks: sourceLinks?.value || [],
          visualization: visualization
        };
      } else {
        throw new Error('No content generated from API');
      }
    } catch (error) {
      console.error('Error generating content from API:', error);
      // Fallback to dummy content if API fails
      return generateDummyContent();
    }
  };

  const generateDummyContent = (): GeneratedContent => {
    const isImageOnly = contentPreferences.format === 'Image Only';
    const isInfographic = contentPreferences.format === 'Infographic';
    const isVideo = contentPreferences.format === 'Video';

    if (isVideo) {
      return {
        title: `${contentPreferences.category} - Video Script`,
        content: `# ${contentPreferences.category} Video Script\n\nThis is a sample video script for ${contentPreferences.category} content. The script would include:\n\n1. Introduction and hook\n2. Main content points\n3. Call to action\n4. Closing remarks\n\n**Target Audience:** ${brandDetails.targetAudience.join(', ')}\n**Brand Voice:** ${brandDetails.brandVoice}\n**Style:** ${contentPreferences.style}`,
        format: contentPreferences.format,
        category: contentPreferences.category,
        followupQuestions: [
          'What are the key takeaways from this video?',
          'How can viewers apply this information?',
          'What additional resources are available?'
        ],
        sourceLinks: [
          'https://example.com/related-resource-1',
          'https://example.com/related-resource-2'
        ]
      };
    }

    if (isInfographic) {
      return {
        title: `${contentPreferences.category} - Infographic`,
        content: `# ${contentPreferences.category} Infographic\n\n## Key Statistics\n- Statistic 1: 85% of companies report improved results\n- Statistic 2: 3x faster implementation\n- Statistic 3: 90% user satisfaction rate\n\n## Visual Elements\n- Charts and graphs\n- Icons and illustrations\n- Color-coded sections\n- Call-out boxes\n\n**Target Audience:** ${brandDetails.targetAudience.join(', ')}\n**Brand Voice:** ${brandDetails.brandVoice}`,
        format: contentPreferences.format,
        category: contentPreferences.category,
        followupQuestions: [
          'What do these statistics mean for businesses?',
          'How can this data be visualized effectively?',
          'What additional metrics would be valuable?'
        ],
        sourceLinks: [
          'https://example.com/data-source-1',
          'https://example.com/data-source-2'
        ]
      };
    }

    if (isImageOnly) {
      return {
        title: `${contentPreferences.category} - Image Content`,
        content: `# ${contentPreferences.category} Image Content\n\n## Image Description\nThis image would showcase ${contentPreferences.category} concepts through visual elements.\n\n## Key Visual Elements\n- Professional photography\n- Branded graphics\n- Clear typography\n- Compelling composition\n\n## Caption\n"${contentPreferences.category} - ${brandDetails.brandVoice} approach to visual storytelling"\n\n**Target Audience:** ${brandDetails.targetAudience.join(', ')}\n**Brand Voice:** ${brandDetails.brandVoice}`,
        format: contentPreferences.format,
        category: contentPreferences.category,
        followupQuestions: [
          'What visual style would best represent this content?',
          'How can the image convey the key message?',
          'What colors and composition would be most effective?'
        ],
        sourceLinks: [
          'https://example.com/visual-inspiration-1',
          'https://example.com/visual-inspiration-2'
        ]
      };
    }

    // Default text content
    return {
      title: `${contentPreferences.category} - ${contentPreferences.format}`,
      content: `# ${contentPreferences.category} - ${contentPreferences.format}\n\n## Overview\nThis ${contentPreferences.format.toLowerCase()} provides comprehensive insights into ${contentPreferences.category} based on the analyzed financial data and conversation history.\n\n## Key Points\n\n### 1. Financial Analysis Summary\nBased on the comprehensive financial data analysis, several key trends and patterns have been identified:\n\n- **Revenue Growth**: Consistent upward trajectory\n- **Cost Management**: Effective optimization strategies\n- **Market Position**: Strong competitive standing\n- **Risk Assessment**: Well-managed risk profile\n\n### 2. Strategic Recommendations\n\n1. **Short-term Actions**\n   - Implement immediate cost optimization measures\n   - Focus on high-impact revenue streams\n   - Strengthen market positioning\n\n2. **Long-term Strategy**\n   - Develop sustainable growth initiatives\n   - Enhance operational efficiency\n   - Build competitive advantages\n\n### 3. Industry Insights\n\n${contentPreferences.category} trends show significant opportunities for growth and development. The analysis reveals:\n\n- Market dynamics favoring strategic positioning\n- Emerging opportunities in key sectors\n- Potential for innovation and expansion\n\n## Conclusion\n\nThe comprehensive analysis of ${contentPreferences.category} data provides valuable insights for strategic decision-making. The recommendations outlined above can help drive sustainable growth and competitive advantage.\n\n**Target Audience:** ${brandDetails.targetAudience.join(', ')}\n**Brand Voice:** ${brandDetails.brandVoice}\n**Content Style:** ${contentPreferences.style}\n**Length:** ${contentPreferences.length}`,
      format: contentPreferences.format,
      category: contentPreferences.category,
      followupQuestions: [
        'What are the most critical insights from this analysis?',
        'How can these recommendations be implemented effectively?',
        'What additional data would strengthen these conclusions?',
        'What are the potential risks and mitigation strategies?'
      ],
      sourceLinks: [
        'https://example.com/financial-analysis-report',
        'https://example.com/industry-benchmarks',
        'https://example.com/strategic-recommendations'
      ]
    };
  };

  const handleGenerateContent = async () => {
    try {
      setIsGeneratingContent(true);

      // Show loading state
      setGeneratedContent({
        title: 'Generating Content...',
        content: 'Please wait while we generate your content...',
        format: contentPreferences.format,
        category: contentPreferences.category
      });
      setShowExportDialog(false);
      setShowContentDisplay(true);

      // Generate content using API
      const content = await generateContentFromAPI();
      setGeneratedContent(content);
    } catch (error) {
      console.error('Error generating content:', error);
      // Fallback to dummy content on error
      const content = generateDummyContent();
      setGeneratedContent(content);
      setShowExportDialog(false);
      setShowContentDisplay(true);
    } finally {
      setIsGeneratingContent(false);
    }
  };

  const handleCopyContent = () => {
    if (generatedContent) {
      const textContent = generateTextContent(generatedContent);
      navigator.clipboard.writeText(textContent);
      setSuccess('Content copied to clipboard!');
    }
  };

  const handleCloseContentDisplay = () => {
    setShowContentDisplay(false);
    setGeneratedContent(null);
  };

  const exportAsIs = async () => {
    try {
      setIsExportingAsIs(true);

      const interactionUIDs = getInteractionUIDs();
      const apiConfig = mapContentPreferencesToAPI();

      const payload = {
        data: {
          interactionUIDs: interactionUIDs,
          instructions: {
            keywords: brandDetails.industryKeywords.split(',').map(k => k.trim()).filter(k => k),
            writing_style: apiConfig.writingStyle,
            tone: apiConfig.tone,
            audience: apiConfig.audience,
            guidelines: apiConfig.guidelines
          },
          contentType: apiConfig.contentType
        },
        agent: {
          code: "EXPORT_CONTENT_FROM_CONVERSATION"
        }
      };

      const response = await axios.post(
        `${environment.controlplaneUrl}/agent/flow/execute`,
        payload,
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.data.status === 200 && response.data.data && response.data.data.length > 0) {
        await generatePDFFromExportData(response.data.data);
        setSuccess('PDF exported successfully!');
      } else {
        throw new Error('No export data received from API');
      }
    } catch (error) {
      console.error('Error exporting conversation:', error);
      setError('Failed to export PDF. Please try again.');
    } finally {
      setIsExportingAsIs(false);
    }
  };

  const generatePDFFromExportData = async (exportData: any[]) => {
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    let yPosition = 20;
    const margin = 20;
    const contentWidth = pageWidth - (margin * 2);

    pdf.setFillColor(156, 39, 176);
    pdf.rect(0, 0, pageWidth, 30, 'F');

    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(24);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Credit Insights Export', margin, 20);

    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'normal');
    pdf.text(`Generated on: ${new Date().toLocaleString()}`, margin, 25);

    pdf.setTextColor(0, 0, 0);
    yPosition = 40;

    for (let i = 0; i < exportData.length; i++) {
      const item = exportData[i];

      if (yPosition > pageHeight - 40) {
        pdf.addPage();
        yPosition = 20;
      }

      pdf.setFillColor(240, 240, 240);
      pdf.rect(margin, yPosition - 5, contentWidth, 15, 'F');

      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.text(`Section ${i + 1}`, margin + 5, yPosition + 5);
      yPosition += 20;

      if (item.entries && Array.isArray(item.entries)) {
        for (const entry of item.entries) {
          if (entry.key === 'geography' || entry.key === 'language' ||
            entry.label === 'Geography' || entry.label === 'Language') {
            continue;
          }

          if (yPosition > pageHeight - 40) {
            pdf.addPage();
            yPosition = 20;
          }

          pdf.setFontSize(12);
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor(156, 39, 176);
          const labelText = `${entry.label}:`;
          pdf.text(labelText, margin, yPosition);
          yPosition += 8;

          pdf.setTextColor(0, 0, 0);

          pdf.setFontSize(10);
          pdf.setFont('helvetica', 'normal');

          let valueText = '';
          if (typeof entry.value === 'string') {
            valueText = entry.value;
          } else if (typeof entry.value === 'object' && entry.value !== null) {
            if (entry.value.headers && entry.value.data) {
              try {
                const chartImage = await generateChartImage(entry.value);
                if (chartImage) {
                  pdf.setFontSize(11);
                  pdf.setFont('helvetica', 'bold');
                  pdf.text(`${entry.value.title || 'Chart'}`, margin, yPosition);
                  yPosition += 8;

                  const imgWidth = Math.min(contentWidth, 160);
                  const imgHeight = (imgWidth * 0.75);

                  if (yPosition + imgHeight > pageHeight - 20) {
                    pdf.addPage();
                    yPosition = 20;
                  }

                  pdf.addImage(chartImage, 'PNG', margin, yPosition, imgWidth, imgHeight);
                  yPosition += imgHeight + 15;

                  pdf.setFontSize(9);
                  pdf.setFont('helvetica', 'normal');
                  pdf.text(`Data points: ${entry.value.data.length}`, margin, yPosition);
                  yPosition += 5;

                  continue;
                }
              } catch (error) {
                console.error('Error generating chart image:', error);
              }

              valueText = `Chart Data: ${entry.value.title || 'Untitled Chart'}`;

              yPosition += 5;
              pdf.setFontSize(9);

              pdf.setFillColor(248, 249, 250);
              pdf.rect(margin, yPosition - 3, contentWidth, 8, 'F');

              const colWidth = contentWidth / entry.value.headers.length;
              let xPos = margin;
              for (const header of entry.value.headers) {
                pdf.setFont('helvetica', 'bold');
                pdf.text(header.substring(0, 15), xPos + 2, yPosition + 2);
                xPos += colWidth;
              }
              yPosition += 8;

              const maxRows = Math.min(8, entry.value.data.length);
              for (let rowIndex = 0; rowIndex < maxRows; rowIndex++) {
                if (yPosition > pageHeight - 20) {
                  pdf.addPage();
                  yPosition = 20;
                }

                const row = entry.value.data[rowIndex];
                xPos = margin;
                for (const header of entry.value.headers) {
                  pdf.setFont('helvetica', 'normal');
                  const cellValue = String(row[header] || '').substring(0, 15);
                  pdf.text(cellValue, xPos + 2, yPosition + 2);
                  xPos += colWidth;
                }
                yPosition += 5;
              }

              if (entry.value.data.length > 8) {
                pdf.text(`... and ${entry.value.data.length - 8} more rows`, margin, yPosition);
                yPosition += 5;
              }

              yPosition += 5;
              continue;
            } else {
              valueText = JSON.stringify(entry.value, null, 2);
            }
          } else {
            valueText = String(entry.value || '');
          }

          const lines = pdf.splitTextToSize(valueText, contentWidth);
          for (const line of lines) {
            if (yPosition > pageHeight - 20) {
              pdf.addPage();
              yPosition = 20;
            }
            pdf.text(line, margin, yPosition);
            yPosition += 5;
          }

          yPosition += 8;
        }
      }

      yPosition += 10;
      pdf.setDrawColor(200, 200, 200);
      pdf.line(margin, yPosition, pageWidth - margin, yPosition);
      yPosition += 15;
    }

    const totalPages = pdf.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      pdf.setPage(i);
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(100, 100, 100);
      pdf.text(`Page ${i} of ${totalPages}`, pageWidth - 30, pageHeight - 10);
      pdf.text('Credit Insights Export', margin, pageHeight - 10);
    }

    const fileName = `credit_insights_export_${new Date().toISOString().split('T')[0]}.pdf`;
    pdf.save(fileName);
  };

  const generateTextContent = (generatedContent: GeneratedContent): string => {
    let text = `${generatedContent.title}\n\n${generatedContent.content}\n\n`;

    if (generatedContent.followupQuestions && generatedContent.followupQuestions.length > 0) {
      text += 'Follow-up Questions:\n';
      generatedContent.followupQuestions.forEach((question, index) => {
        text += `${index + 1}. ${question}\n`;
      });
      text += '\n';
    }

    if (generatedContent.sourceLinks && generatedContent.sourceLinks.length > 0) {
      text += 'Source Links:\n';
      generatedContent.sourceLinks.forEach((link, index) => {
        text += `${index + 1}. ${link}\n`;
      });
    }

    return text;
  };

  // PDF generation functions
  const generatePDFFromGeneratedContent = async (generatedContent: GeneratedContent) => {
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    let yPosition = 20;
    const margin = 20;
    const contentWidth = pageWidth - (margin * 2);

    // Add header with styling
    pdf.setFillColor(156, 39, 176); // Purple background
    pdf.rect(0, 0, pageWidth, 30, 'F');

    pdf.setTextColor(255, 255, 255); // White text
    pdf.setFontSize(24);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Generated Content Export', margin, 20);

    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'normal');
    pdf.text(`Generated on: ${new Date().toLocaleString()}`, margin, 25);

    // Reset text color
    pdf.setTextColor(0, 0, 0);
    yPosition = 40;

    // Add title
    pdf.setFontSize(18);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(156, 39, 176);
    pdf.text(generatedContent.title, margin, yPosition);
    yPosition += 15;

    // Add content
    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Content:', margin, yPosition);
    yPosition += 10;

    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    const contentLines = pdf.splitTextToSize(generatedContent.content, contentWidth);
    for (const line of contentLines) {
      if (yPosition > pageHeight - 20) {
        pdf.addPage();
        yPosition = 20;
      }
      pdf.text(line, margin, yPosition);
      yPosition += 5;
    }

    // Add visualization if present
    if (generatedContent.visualization) {
      yPosition += 10;

      if (yPosition > pageHeight - 40) {
        pdf.addPage();
        yPosition = 20;
      }

      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(156, 39, 176);
      pdf.text('Data Visualization:', margin, yPosition);
      yPosition += 10;

      // Generate chart image
      try {
        const chartImage = await generateChartImage(generatedContent.visualization.data);
        if (chartImage) {
          const imgWidth = Math.min(contentWidth, 160);
          const imgHeight = (imgWidth * 0.75);

          if (yPosition + imgHeight > pageHeight - 20) {
            pdf.addPage();
            yPosition = 20;
          }

          pdf.addImage(chartImage, 'PNG', margin, yPosition, imgWidth, imgHeight);
          yPosition += imgHeight + 15;
        }
      } catch (error) {
        console.error('Error generating chart image:', error);
        // Fallback to table
        yPosition += 5;
        pdf.setFontSize(9);

        const headers = generatedContent.visualization.data.headers || [];
        const data = generatedContent.visualization.data.data || [];

        if (headers.length > 0 && data.length > 0) {
          const colWidth = contentWidth / headers.length;
          let xPos = margin;

          // Add headers
          for (const header of headers) {
            pdf.setFont('helvetica', 'bold');
            pdf.text(header.substring(0, 15), xPos + 2, yPosition + 2);
            xPos += colWidth;
          }
          yPosition += 8;

          // Add data rows
          const maxRows = Math.min(8, data.length);
          for (let rowIndex = 0; rowIndex < maxRows; rowIndex++) {
            if (yPosition > pageHeight - 20) {
              pdf.addPage();
              yPosition = 20;
            }

            const row = data[rowIndex];
            xPos = margin;
            for (const header of headers) {
              pdf.setFont('helvetica', 'normal');
              const cellValue = String(row[header] || '').substring(0, 15);
              pdf.text(cellValue, xPos + 2, yPosition + 2);
              xPos += colWidth;
            }
            yPosition += 5;
          }
        }
      }
    }

    // Add source links if present
    if (generatedContent.sourceLinks && generatedContent.sourceLinks.length > 0) {
      yPosition += 10;

      if (yPosition > pageHeight - 40) {
        pdf.addPage();
        yPosition = 20;
      }

      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(156, 39, 176);
      pdf.text('Source Links:', margin, yPosition);
      yPosition += 10;

      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(0, 0, 0);

      for (const link of generatedContent.sourceLinks) {
        if (yPosition > pageHeight - 20) {
          pdf.addPage();
          yPosition = 20;
        }
        pdf.text(`• ${link}`, margin, yPosition);
        yPosition += 5;
      }
    }

    // Add footer
    const totalPages = pdf.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      pdf.setPage(i);
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(100, 100, 100);
      pdf.text(`Page ${i} of ${totalPages}`, pageWidth - 30, pageHeight - 10);
      pdf.text('Generated Content Export', margin, pageHeight - 10);
    }

    // Save the PDF
    const fileName = `${generatedContent.title.replace(/[^a-z0-9]/gi, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
    pdf.save(fileName);
  };

  const generateChartImage = async (chartData: any): Promise<string | null> => {
    try {
      // Validate chart data
      if (!chartData || !chartData.headers || !chartData.data || chartData.data.length === 0) {
        console.warn('Invalid chart data provided:', chartData);
        return await generateTableChartImage(chartData);
      }

      // Create a temporary container for the chart
      const tempContainer = document.createElement('div');
      tempContainer.style.position = 'absolute';
      tempContainer.style.left = '-9999px';
      tempContainer.style.top = '-9999px';
      tempContainer.style.width = '600px';
      tempContainer.style.height = '400px';
      tempContainer.style.backgroundColor = 'white';
      tempContainer.style.padding = '20px';
      tempContainer.style.borderRadius = '8px';
      tempContainer.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
      document.body.appendChild(tempContainer);

      // Create a React root to render the chart component
      const { createRoot } = await import('react-dom/client');
      const root = createRoot(tempContainer);

      // Render the same DataChart component used in chatbot
      const ChartComponent = () => {
        return (
          <div style={{ width: '100%', height: '100%' }}>
            {chartData.visual ? (
              <DataChart data={chartData} />
            ) : chartData.title && chartData.title.toLowerCase().includes('sentiment') ? (
              // Special handling for sentiment charts - use pie chart
              <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <h3 style={{ color: '#4c3794', marginBottom: '20px', fontSize: '18px', fontWeight: '600' }}>
                  {chartData.title}
                </h3>
                <div style={{ width: '300px', height: '250px' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={chartData.data}
                        dataKey={chartData.headers[1]}
                        nameKey={chartData.headers[0]}
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        innerRadius={40}
                        fill="#4c3794"
                        label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                        labelLine={false}
                      >
                        {chartData.data.map((entry: any, index: number) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={
                              entry[chartData.headers[0]] === 'Positive' ? '#4c3794' :
                                entry[chartData.headers[0]] === 'Negative' ? '#b19cd9' :
                                  '#d8bfd8'
                            }
                          />
                        ))}
                      </Pie>
                      <RechartsTooltip
                        formatter={(value: number, name: string) => [`${value}`, name]}
                        contentStyle={{
                          backgroundColor: '#ede7f6',
                          borderRadius: '8px',
                          border: 'none',
                          boxShadow: '0 4px 12px rgba(76, 55, 148, 0.1)',
                          padding: '8px 12px',
                          color: '#4c3794'
                        }}
                      />
                      <Legend
                        verticalAlign="bottom"
                        height={36}
                        formatter={(value) => (
                          <span style={{ color: '#4c3794', fontSize: '12px' }}>{value}</span>
                        )}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            ) : (
              <DataTable data={chartData} />
            )}
          </div>
        );
      };

      // Render the chart
      root.render(<ChartComponent />);

      // Wait a bit for the chart to render
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Convert to image using html2canvas
      const canvas = await html2canvas(tempContainer, {
        backgroundColor: 'white',
        scale: 2,
        useCORS: true,
        allowTaint: true,
        width: tempContainer.offsetWidth,
        height: tempContainer.offsetHeight
      });

      const imageDataUrl = canvas.toDataURL('image/png');

      // Clean up
      root.unmount();
      document.body.removeChild(tempContainer);

      return imageDataUrl;
    } catch (error) {
      console.error('Error generating chart image:', error);
      return null;
    }
  };

  const generateTableChartImage = async (chartData: any): Promise<string | null> => {
    try {
      const chartElement = createTableChart(chartData);
      const canvas = await html2canvas(chartElement);
      const imageDataUrl = canvas.toDataURL('image/png');
      return imageDataUrl;
    } catch (error) {
      console.error('Error generating table chart image:', error);
      return null;
    }
  };

  const createTableChart = (chartData: any): HTMLElement => {
    const container = document.createElement('div');
    container.style.width = '800px';
    container.style.height = '400px';
    container.style.position = 'absolute';
    container.style.left = '-9999px';
    container.style.top = '-9999px';
    container.style.backgroundColor = 'white';
    container.style.padding = '20px';
    container.style.borderRadius = '8px';
    container.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';

    const title = document.createElement('h3');
    title.textContent = chartData.title || 'Data Table';
    title.style.color = '#4c3794';
    title.style.marginBottom = '20px';
    title.style.fontSize = '18px';
    title.style.fontWeight = '600';
    container.appendChild(title);

    const table = document.createElement('table');
    table.style.width = '100%';
    table.style.borderCollapse = 'collapse';
    table.style.fontSize = '12px';

    // Create header
    const thead = document.createElement('thead');
    const headerRow = document.createElement('tr');
    headerRow.style.backgroundColor = 'rgba(76, 55, 148, 0.05)';

    chartData.headers.forEach((header: string) => {
      const th = document.createElement('th');
      th.textContent = header;
      th.style.padding = '8px 12px';
      th.style.textAlign = 'left';
      th.style.fontWeight = '600';
      th.style.color = '#4c3794';
      th.style.borderBottom = '1px solid rgba(76, 55, 148, 0.1)';
      headerRow.appendChild(th);
    });

    thead.appendChild(headerRow);
    table.appendChild(thead);

    // Create body
    const tbody = document.createElement('tbody');
    chartData.data.forEach((row: any, index: number) => {
      const tr = document.createElement('tr');
      tr.style.backgroundColor = index % 2 === 0 ? '#fff' : 'rgba(76, 55, 148, 0.02)';

      chartData.headers.forEach((header: string) => {
        const td = document.createElement('td');
        td.textContent = String(row[header] || '');
        td.style.padding = '8px 12px';
        td.style.borderBottom = '1px solid rgba(18, 5, 62, 0.05)';
        td.style.color = '#333';
        tr.appendChild(td);
      });

      tbody.appendChild(tr);
    });

    table.appendChild(tbody);
    container.appendChild(table);

    return container;
  };

  const handleDownloadContent = async () => {
    if (generatedContent) {
      try {
        await generatePDFFromGeneratedContent(generatedContent);
        setSuccess('Content downloaded as PDF!');
      } catch (error) {
        console.error('Error downloading content:', error);
        setError('Failed to download content. Please try again.');
      }
    }
  };

  // Fetch content types
  const fetchContentTypes = async () => {
    setInitialLoadingState(prev => ({ ...prev, contentTypes: true }));
    try {
      const response = await axios.post(
        `${environment.apiUrl}/account/getContentTypes`,
        {
          data: {}
        },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data && Array.isArray(response.data)) {
        setContentTypes(response.data);
      }
    } catch (error: any) {
      console.error('Error fetching content types:', error);
      if (error?.response?.data?.error === 'jwt malformed' || error?.response?.data?.error === 'jwt expired') {
        navigate('/login');
      }
      // Set fallback content types if API fails
      setContentTypes([
        { contentType: 'SOCIAL_POST_LINKEDIN', contentTypeDescription: 'LinkedIn Post', class: 'Semi Formal' },
        { contentType: 'SOCIAL_POST_FACEBOOK', contentTypeDescription: 'Facebook Post', class: 'Semi Formal' },
        { contentType: 'SOCIAL_POST_TWITTER', contentTypeDescription: 'Twitter Post', class: 'Semi Formal' },
        { contentType: 'BLOG_POST', contentTypeDescription: 'Blog', class: 'Semi Formal' },
        { contentType: 'EMAIL', contentTypeDescription: 'Email', class: 'Semi Formal' },
        { contentType: 'CUSTOM', contentTypeDescription: 'Custom', class: 'Semi Formal' }
      ]);
    } finally {
      setInitialLoadingState(prev => ({ ...prev, contentTypes: false }));
    }
  };

  return (
    <Box sx={{ padding: '10px' }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '24px',
          padding: '16px', // Add padding to ensure the content doesn't touch the edges
          borderBottom: '1px solid #E0E0E0', // Subtle divider for visual separation
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          {/* Back Button and Icon */}
          <Box
            sx={{
              height: '48px',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#F5F5F5', // Slight background for a more polished look
              padding: '0 16px',
            }}
          >
            <Button
              startIcon={<ArrowBackIosIcon sx={{ fontSize: 18 }} />}
              onClick={() => {
                // Check if we have a stored navigation context
                const context = sessionStorage.getItem('appsNavigationContext');
                if (context && context.startsWith('industry:')) {
                  // Navigate back to Apps with the stored context
                  navigate('/Apps');
                } else {
                  // Default navigation to Apps
                  navigate('/Apps');
                }
              }}
              sx={{
                color: '#4c3794',
                fontWeight: 600,
                fontSize: '0.875rem',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: 'rgba(76, 55, 148, 0.1)',
                },
              }}
            >
              Back
            </Button>
            <BusinessIcon
              sx={{ fontSize: 24, color: '#4c3794', marginLeft: '24px' }}
            />
          </Box>

          {/* Title */}
          <Typography
            variant="h5"
            sx={{
              color: '#131F32',
              fontWeight: 600,
              fontSize: '1.25rem',
              marginLeft: '16px', // Slight spacing for separation
            }}
          >
            Company Research
          </Typography>
        </Box>

        {/* Right Section (Upload Button and Project Selector) */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* Upload Document Button - Disabled if user lacks edit permission */}
          <Tooltip title={!canEditProject ? "Edit permission required to upload documents" : ""}>
            <span>
              <Button
                variant="contained"
                startIcon={<UploadFileIcon />}
                onClick={() => setUploadDialogOpen(true)}
                disabled={!canEditProject}
                sx={{
                  backgroundColor: canEditProject ? '#4c3794' : 'rgba(76, 55, 148, 0.3)',
                  '&:hover': {
                    backgroundColor: canEditProject ? '#3a2a73' : 'rgba(76, 55, 148, 0.3)',
                  },
                  '&.Mui-disabled': {
                    backgroundColor: 'rgba(76, 55, 148, 0.3)',
                    color: '#ffffff'
                  },
                  textTransform: 'none',
                  fontWeight: 600,
                }}
              >
                Upload Document
              </Button>
            </span>
          </Tooltip>
          {/* Project Selector */}
          <FormControl sx={{ minWidth: 220 }} size="small">
            <InputLabel>Select Project</InputLabel>
            <Select
              value={selectedProject?.id || ''}
              onChange={e => handleProjectChange(e.target.value)}
              label="Select Project"
              disabled={isLoadingProjects}
            >
              {projects.map(project => (
                <MenuItem key={project.id} value={project.id}>
                  {project.project_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setIsProjectDialogOpen(true)}
            sx={{
              backgroundColor: '#4c3794',
              '&:hover': { backgroundColor: '#4c3794' },
              fontWeight: 600
            }}
          >
            Create Project
          </Button>

        </Box>
      </Box>

      {/* Top-Level Tabs */}


      {/* Access Denied Check */}
      {!canViewProject && selectedProject ? (
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          textAlign: 'center',
          p: 4,
          backgroundColor: '#fafafa',
          borderRadius: 2,
          mx: 2
        }}>
          <LockIcon sx={{ fontSize: 64, color: '#f44336', mb: 2 }} />
          <Typography variant="h4" sx={{ mb: 2, color: '#333' }}>
            Access Denied
          </Typography>
          <Typography variant="h6" sx={{ mb: 3, color: '#666' }}>
            You don't have access to this project
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, color: '#666' }}>
            Please create a new project or select another project that you have access to.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setIsProjectDialogOpen(true)}
              sx={{
                backgroundColor: '#4c3794',
                '&:hover': { backgroundColor: '#388E3C' },
                fontWeight: 600
              }}
            >
              Create New Project
            </Button>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => window.location.reload()}
              sx={{
                borderColor: '#4c3794',
                color: '#4c3794',
                '&:hover': {
                  borderColor: '#388E3C',
                  backgroundColor: 'rgba(76, 175, 80, 0.04)'
                },
                fontWeight: 600
              }}
            >
              Refresh
            </Button>
          </Box>
        </Box>
      ) : (
        <>
          {/* Main Content */}
          {isFullScreen ? (
            <FullScreenContainer>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 3,
                  pb: 2,
                  borderBottom: '1px solid #E0E0E0',
                  flexShrink: 0,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      overflow: 'hidden',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      border: '2px solid #fff',
                    }}
                  >
                    <img
                      src={botIcon}
                      alt="bot"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  </Box>
                  <Box>
                    <Typography
                      variant="h6"
                      sx={{
                        color: '#131F32',
                        fontWeight: 600,
                        fontSize: '0.875rem',
                      }}
                    >
                      AI Assistant
                    </Typography>
                    <Typography
                      variant="subtitle2"
                      sx={{ color: '#64748B', fontSize: '0.75rem' }}
                    >
                      Ask me about {((selectedProject || projects[0]?.project_name) || 'Untitled Project').toString()}'s
                      financials
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {!isExportMode && (
                    <Button
                      onClick={handleExportMode}
                      size="small"
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      sx={{
                        color: '#4c3794',
                        borderColor: '#4c3794',
                        fontSize: '0.75rem',
                        textTransform: 'none',
                        '&:hover': {
                          backgroundColor: 'rgba(76, 55, 148, 0.05)',
                          borderColor: '#4c3794',
                        },
                      }}
                    >
                      Export Content
                    </Button>
                  )}
                  <IconButton
                    onClick={() => setIsFullScreen(false)}
                    sx={{
                      color: '#4c3794',
                      '&:hover': {
                        backgroundColor: 'rgba(76, 55, 148, 0.1)',
                      },
                    }}
                  >
                    <FullscreenExitIcon />
                  </IconButton>
                </Box>
              </Box>
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                {/* Chat Panel Header with Close Button */}
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  mb: 2,
                  pb: 1.5,
                  borderBottom: '1px solid #e9ecef'
                }}>
                  <IconButton
                    onClick={() => setIsFullScreen(false)}
                    sx={{
                      color: '#64748B',
                      '&:hover': {
                        backgroundColor: 'rgba(76, 55, 148, 0.1)',
                        color: '#4c3794'
                      }
                    }}
                    aria-label="Close chat"
                  >
                    <CloseIcon />
                  </IconButton>
                </Box>
                <Chatbot
                  placeholder="Type your message..."
                  welcomeMessage="Hello! I'm your AI assistant. How can I help you analyze the company's financial data?"
                  enableVoice={true}
                  enableGovAPI={true}
                  apiConfig={chatbotApiConfig}
                  getAuthToken={async () => localStorage.getItem('token')}
                />
              </Box>
            </FullScreenContainer>
          ) : (
            <Box sx={{ display: 'flex', gap: '24px', overflow: 'hidden', width: '100%', maxWidth: '100vw' }}>
              {/* Main Content Area */}
              <Box sx={{ width: isChatOpen ? '65%' : '100%', minWidth: 0, overflow: 'hidden', transition: 'width 0.3s ease-in-out' }}>
                <>


                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    mb: 3,
                    px: 2
                  }}>
                    <Tabs
                      value={topLevelTab}
                      onChange={(e, newValue) => setTopLevelTab(newValue)}
                      sx={{
                        '& .MuiTab-root': {
                          textTransform: 'none',
                          fontWeight: 600,
                          fontSize: '0.875rem',
                          minWidth: 'auto',
                          px: 3,
                          py: 1.5,
                          color: '#666',
                          '&.Mui-selected': {
                            color: '#4c3794',
                          },
                        },
                        '& .MuiTabs-indicator': {
                          backgroundColor: '#4c3794',
                          height: 3,
                          borderRadius: '2px 2px 0 0',
                        },
                      }}
                    >
                      <Tab
                        label="Dashboard"
                        icon={<DashboardIcon sx={{ fontSize: 18, mb: 0.5 }} />}
                        iconPosition="top"
                      />
                      <Tab
                        label="AI Summary"
                        icon={<InfoIcon sx={{ fontSize: 18, mb: 0.5 }} />}
                        iconPosition="top"
                      />
                      <Tab
                        label="Data Categories"
                        icon={<AssessmentIcon sx={{ fontSize: 18, mb: 0.5 }} />}
                        iconPosition="top"
                      />
                    </Tabs>
                  </Box>




                  {/* Dashboard Tab */}
                  {topLevelTab === 0 && (
                    <Box sx={{ height: 'calc(100vh - 280px)', overflowY: 'auto', position: 'relative' }}>
                      {isInitialLoading && (
                        <Box sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          minHeight: '400px',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          backgroundColor: 'rgba(255, 255, 255, 0.9)',
                          zIndex: 10
                        }}>
                          <Box sx={{ textAlign: 'center' }}>
                            <CircularProgress sx={{ color: '#4c3794', mb: 2 }} size={50} />
                            <Typography sx={{ color: '#4c3794', fontWeight: 600 }}>
                              Loading data...
                            </Typography>
                          </Box>
                        </Box>
                      )}
                      {!isInitialLoading && (
                        <Dashboard comprehensiveData={comprehensiveData} dataFetchProgress={dataFetchProgress} />
                      )}
                    </Box>
                  )}

                  {/* AI Summary Tab */}
                  {topLevelTab === 1 && (
                    <AISummaryTab comprehensiveData={comprehensiveData} dataFetchProgress={dataFetchProgress} />
                  )}

                  {/* Data Categories Tab */}
                  {topLevelTab === 2 && (
                    <Box sx={{ display: 'flex', height: 'calc(100vh - 280px)', width: '100%', overflow: 'hidden' }}>
                      {/* Sidebar - Category Navigation */}
                      <Box sx={{
                        width: sidebarCollapsed ? 80 : 280,
                        borderRight: '1px solid #e0e0e0',
                        backgroundColor: '#f8f9fa',
                        overflowY: 'auto',
                        borderRadius: '16px 0 0 16px',
                        transition: 'width 0.3s ease-in-out',
                        position: 'relative'
                      }}>
                        {/* Collapse/Expand Button */}
                        <Box sx={{
                          position: 'absolute',
                          top: 16,
                          right: 8,
                          zIndex: 10,
                          backgroundColor: '#4c3794',
                          borderRadius: '50%',
                          width: 32,
                          height: 32,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            backgroundColor: '#388E3C',
                            transform: 'scale(1.1)',
                            boxShadow: '0 6px 16px rgba(76, 175, 80, 0.4)'
                          }
                        }}
                          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                        >
                          {sidebarCollapsed ?
                            <ChevronRightIcon sx={{ fontSize: 20, color: '#fff' }} /> :
                            <ChevronLeftIcon sx={{ fontSize: 20, color: '#fff' }} />
                          }
                        </Box>

                        <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0', pr: sidebarCollapsed ? 2 : 6 }}>
                          {!sidebarCollapsed && (
                            <>
                              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                                Data Categories
                              </Typography>
                              <Typography variant="body2" sx={{ color: '#666' }}>
                                Select a category to view detailed financial information
                              </Typography>
                            </>
                          )}
                        </Box>

                        <Box sx={{ p: 1 }}>
                          {INFORMATION_CATEGORIES
                            .filter((category) => {
                              // Remove Dashboard and Others from sidebar
                              if (category.informationCategory === 'DASHBOARD' || category.informationCategory === 'OTHER') {
                                return false;
                              }

                              const categoryData = comprehensiveData.find(d => d.category === category.informationCategory);
                              const hasData = categoryData?.data && categoryData.data.length > 0;

                              // For other categories, only show if they have data
                              return hasData;
                            })
                            .map((category) => {
                              const categoryData = comprehensiveData.find(d => d.category === category.informationCategory);
                              const hasData = categoryData?.data && categoryData.data.length > 0;
                              const hasError = categoryData?.error;
                              const isSelected = selectedCategory === category.informationCategory;

                              return (
                                <Tooltip
                                  key={category.informationCategory}
                                  title={sidebarCollapsed ? category.informationCategory.replace(/_/g, ' ') : ''}
                                  placement="right"
                                >
                                  <Box
                                    onClick={() => setSelectedCategory(category.informationCategory)}
                                    sx={{
                                      p: sidebarCollapsed ? 1.5 : 2,
                                      mb: 1,
                                      borderRadius: 1,
                                      cursor: 'pointer',
                                      backgroundColor: isSelected ? 'rgba(76, 55, 148, 0.1)' : 'transparent',
                                      border: isSelected ? '1px solid #4c3794' : '1px solid transparent',
                                      display: 'flex',
                                      alignItems: sidebarCollapsed ? 'center' : 'flex-start',
                                      justifyContent: sidebarCollapsed ? 'center' : 'space-between',
                                      flexDirection: sidebarCollapsed ? 'column' : 'row',
                                      minHeight: sidebarCollapsed ? 60 : 'auto',
                                      '&:hover': {
                                        backgroundColor: isSelected ? 'rgba(76, 55, 148, 0.1)' : '#f0f0f0'
                                      }
                                    }}
                                  >
                                    {sidebarCollapsed ? (
                                      // Collapsed view - show only icon
                                      <Box sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        gap: 0.5
                                      }}>
                                        <Box sx={{
                                          color: isSelected ? '#4c3794' : '#666',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center'
                                        }}>
                                          {category.icon}
                                        </Box>
                                        {/* Status indicator */}
                                        <Box sx={{
                                          width: 6,
                                          height: 6,
                                          borderRadius: '50%',
                                          backgroundColor: dataFetchProgress[category.informationCategory] ? '#FFA726' :
                                            hasData ? '#4c3794' : hasError ? '#f44336' : '#ccc'
                                        }} />
                                      </Box>
                                    ) : (
                                      // Expanded view - show full content
                                      <>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flex: 1 }}>
                                          <Box sx={{
                                            color: isSelected ? '#4c3794' : '#666',
                                            display: 'flex',
                                            alignItems: 'center'
                                          }}>
                                            {category.icon}
                                          </Box>
                                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                            {category.informationCategory.replace(/_/g, ' ')}
                                          </Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                          {dataFetchProgress[category.informationCategory] && (
                                            <Box sx={{
                                              width: 8,
                                              height: 8,
                                              borderRadius: '50%',
                                              backgroundColor: '#FFA726'
                                            }} />
                                          )}
                                          {!dataFetchProgress[category.informationCategory] && hasData && (
                                            <Box sx={{
                                              width: 8,
                                              height: 8,
                                              borderRadius: '50%',
                                              mt: .5,
                                              mr: 1,
                                              backgroundColor: '#4c3794'
                                            }} />
                                          )}
                                          {!dataFetchProgress[category.informationCategory] && hasError && (
                                            <Box sx={{
                                              width: 8,
                                              height: 8,
                                              borderRadius: '50%',
                                              backgroundColor: '#f44336'
                                            }} />
                                          )}
                                          {!dataFetchProgress[category.informationCategory] && !hasData && !hasError && (
                                            <Box sx={{
                                              width: 8,
                                              height: 8,
                                              borderRadius: '50%',
                                              backgroundColor: '#ccc'
                                            }} />
                                          )}
                                        </Box>
                                        {dataFetchProgress[category.informationCategory] && (
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <CircularProgress size={12} sx={{ color: '#4c3794' }} />
                                            <Typography variant="caption" sx={{ color: '#4c3794', fontSize: '0.7rem' }}>
                                              Loading...
                                            </Typography>
                                          </Box>
                                        )}
                                        {!dataFetchProgress[category.informationCategory] && hasData && (
                                          <Typography variant="caption" sx={{ color: '#4c3794', fontWeight: 500 }}>
                                            {categoryData?.data?.length || 0}
                                          </Typography>
                                        )}
                                        {!dataFetchProgress[category.informationCategory] && hasError && (
                                          <Typography variant="caption" sx={{ color: '#f44336' }}>
                                            Error loading data
                                          </Typography>
                                        )}
                                      </>
                                    )}
                                  </Box>
                                </Tooltip>
                              );
                            })}
                        </Box>
                      </Box>

                      {/* Main Content Area */}
                      <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', p: 3, backgroundColor: '#fff', borderRadius: '0 16px 16px 0', minWidth: 0 }}>
                        {(() => {
                          const selectedCategoryData = comprehensiveData.find(d => d.category === selectedCategory);
                          const categoryInfo = INFORMATION_CATEGORIES.find(c => c.informationCategory === selectedCategory);

                          if (!selectedCategoryData || !categoryInfo) {
                            return (
                              <Box sx={{ textAlign: 'center', mt: 4 }}>
                                <Typography variant="h6" sx={{ color: '#666' }}>
                                  Select a category from the sidebar
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#999', mt: 1 }}>
                                  Choose a data category to view detailed information
                                </Typography>
                              </Box>
                            );
                          }

                          if (!selectedCategoryData.data || selectedCategoryData.data.length === 0) {
                            return (
                              <Box sx={{ textAlign: 'center', mt: 4 }}>
                                <Typography variant="h6" sx={{ color: '#666' }}>
                                  No data available for {categoryInfo.informationCategory.replace(/_/g, ' ')}
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#999', mt: 1 }}>
                                  Data will appear here when available
                                </Typography>
                              </Box>
                            );
                          }

                          return (
                            <Box>
                              {/* Category Header */}
                              <Box sx={{ mb: 3 }}>
                                <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                                  {categoryInfo.informationCategory.replace(/_/g, ' ')}
                                </Typography>
                                <Typography variant="body1" sx={{ color: '#666', mb: 2 }}>
                                  {categoryInfo.informationCategoryName}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                  {categoryInfo.columns.map((column) => (
                                    <Chip
                                      key={column}
                                      label={column.replace(/_/g, ' ')}
                                      size="small"
                                      sx={{
                                        backgroundColor: 'rgba(76, 175, 80, 0.1)',
                                        color: '#4c3794',
                                        fontSize: '0.75rem'
                                      }}
                                    />
                                  ))}
                                </Box>
                              </Box>

                              {/* Data Table */}
                              <Card sx={{
                                borderRadius: 2,
                                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                overflow: 'hidden'
                              }}>
                                <Box sx={{ overflowX: 'auto' }}>
                                  <table style={{
                                    width: '100%',
                                    borderCollapse: 'collapse',
                                    fontSize: '0.875rem'
                                  }}>
                                    <thead>
                                      <tr style={{ backgroundColor: 'rgba(76, 175, 80, 0.05)' }}>
                                        <th style={{
                                          padding: '16px',
                                          textAlign: 'left',
                                          fontWeight: 600,
                                          color: '#4c3794',
                                          borderBottom: '2px solid rgba(76, 175, 80, 0.1)',
                                          fontSize: '0.875rem',
                                          minWidth: '120px'
                                        }}>
                                          Record
                                        </th>
                                        {categoryInfo.columns.map((column) => (
                                          <th key={column} style={{
                                            padding: '16px',
                                            textAlign: 'left',
                                            fontWeight: 600,
                                            color: '#4c3794',
                                            borderBottom: '2px solid rgba(76, 175, 80, 0.1)',
                                            fontSize: '0.875rem',
                                            minWidth: '150px'
                                          }}>
                                            {column.replace(/_/g, ' ')}
                                          </th>
                                        ))}
                                        <th style={{
                                          padding: '16px',
                                          textAlign: 'left',
                                          fontWeight: 600,
                                          color: '#4c3794',
                                          borderBottom: '2px solid rgba(76, 175, 80, 0.1)',
                                          fontSize: '0.875rem',
                                          minWidth: '120px'
                                        }}>
                                          Published Date
                                        </th>
                                        <th style={{
                                          padding: '16px',
                                          textAlign: 'left',
                                          fontWeight: 600,
                                          color: '#4c3794',
                                          borderBottom: '2px solid rgba(76, 175, 80, 0.1)',
                                          fontSize: '0.875rem',
                                          minWidth: '100px'
                                        }}>
                                          Risk Rating
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {selectedCategoryData.data.map((item, index) => {
                                        if (item.objectType === 'DICTIONARY' && item.entries) {
                                          const entries = item.entries.reduce((acc: any, entry: any) => {
                                            acc[entry.key] = entry.value;
                                            return acc;
                                          }, {});

                                          return (
                                            <tr key={index} style={{
                                              backgroundColor: index % 2 === 0 ? '#fff' : 'rgba(76, 175, 80, 0.02)'
                                            }}>
                                              <td style={{
                                                padding: '16px',
                                                borderBottom: '1px solid rgba(76, 175, 80, 0.05)',
                                                color: '#333',
                                                fontSize: '0.875rem',
                                                fontWeight: 500
                                              }}>
                                                {entries.title || entries.financialYear || `Record ${index + 1}`}
                                              </td>
                                              {categoryInfo.columns.map((column) => {
                                                const value = entries[column];
                                                return (
                                                  <td key={column} style={{
                                                    padding: '16px',
                                                    borderBottom: '1px solid rgba(76, 175, 80, 0.05)',
                                                    color: '#333',
                                                    fontSize: '0.875rem'
                                                  }}>
                                                    {value !== undefined && value !== null && value !== '' ?
                                                      (typeof value === 'number' ? value.toLocaleString() : value) :
                                                      '-'
                                                    }
                                                  </td>
                                                );
                                              })}
                                              <td style={{
                                                padding: '16px',
                                                borderBottom: '1px solid rgba(76, 175, 80, 0.05)',
                                                color: '#666',
                                                fontSize: '0.875rem'
                                              }}>
                                                {entries.publishedDate ?
                                                  new Date(entries.publishedDate).toLocaleDateString() :
                                                  '-'
                                                }
                                              </td>
                                              <td style={{
                                                padding: '16px',
                                                borderBottom: '1px solid rgba(76, 175, 80, 0.05)',
                                                fontSize: '0.875rem'
                                              }}>
                                                {entries.contentRating ? (
                                                  <span style={{
                                                    padding: '4px 8px',
                                                    borderRadius: '4px',
                                                    fontSize: '0.75rem',
                                                    fontWeight: 500,
                                                    backgroundColor: entries.contentRating.rating === 'Low Risk' ? '#E8F5E9' :
                                                      entries.contentRating.rating === 'Medium Risk' ? '#FFF3E0' : '#FBE9E7',
                                                    color: entries.contentRating.rating === 'Low Risk' ? '#2E7D32' :
                                                      entries.contentRating.rating === 'Medium Risk' ? '#F57C00' : '#D32F2F'
                                                  }}>
                                                    {entries.contentRating.rating}
                                                  </span>
                                                ) : '-'}
                                              </td>
                                            </tr>
                                          );
                                        }
                                        return null;
                                      })}
                                    </tbody>
                                  </table>
                                </Box>
                              </Card>
                            </Box>
                          );
                        })()}
                      </Box>
                    </Box>
                  )}
                </>
              </Box>

              {/* Right Panel - Chat Interface */}
              {isChatOpen && (
                <Box sx={{
                  width: '35%',
                  flexShrink: 0,
                  overflow: 'hidden'
                }}>
                  <Paper
                    sx={{
                      p: 3,
                      borderRadius: '16px',
                      height: 'calc(100vh - 180px)',
                      display: 'flex',
                      flexDirection: 'column',
                      overflow: 'hidden',
                      width: '100%'
                    }}
                  >
                    {/* Chat Panel Header with Close Button */}
                    <Box sx={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      alignItems: 'center',
                      mb: 2,
                      pb: 1.5,
                      borderBottom: '1px solid #e9ecef'
                    }}>
                      <IconButton
                        onClick={handleCloseChat}
                        sx={{
                          color: '#64748B',
                          '&:hover': {
                            backgroundColor: 'rgba(76, 55, 148, 0.1)',
                            color: '#4c3794'
                          }
                        }}
                        aria-label="Close chat"
                      >
                        <CloseIcon />
                      </IconButton>
                    </Box>
                    {/* Chat Interface - Using Reusable Chatbot */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', flex: 1 }}>
                      <Chatbot
                        placeholder="Type your message..."
                        welcomeMessage="Hello! I'm your AI assistant. How can I help you analyze the company's financial data?"
                        enableVoice={true}
                        enableGovAPI={true}
                        apiConfig={chatbotApiConfig}
                        getAuthToken={async () => localStorage.getItem('token')}
                      />
                    </Box>
                  </Paper>
                </Box>
              )}
            </Box>
          )}
        </>
      )}

      {/* Project Creation Dialog */}
      <Dialog
        open={isProjectDialogOpen}
        onClose={() => setIsProjectDialogOpen(false)}
        maxWidth="md"
        fullWidth
        sx={{
          zIndex: 9999,
          '& .MuiDialog-paper': {
            zIndex: 9999
          }
        }}
      >
        <DialogTitle sx={{
          pb: 1,
          borderBottom: '1px solid #E5E9EF',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#131F32' }}>
            Create New Project
          </Typography>
          <IconButton
            onClick={() => setIsProjectDialogOpen(false)}
            sx={{ color: '#64748B' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={3} pt={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Project Name *"
                value={newProject.project_name}
                onChange={(e) => setNewProject(prev => ({ ...prev, project_name: e.target.value }))}
                size="small"
                error={!!projectError && !newProject.project_name.trim()}
                helperText={projectError && !newProject.project_name.trim() ? 'Project name is required' : ''}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Project Type"
                value={newProject.project_type}
                onChange={(e) => setNewProject(prev => ({ ...prev, project_type: e.target.value }))}
                size="small"
                disabled
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Project Description"
                value={newProject.project_description}
                onChange={(e) => setNewProject(prev => ({ ...prev, project_description: e.target.value }))}
                multiline
                rows={3}
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={newProject.period_start_date}
                onChange={(e) => setNewProject(prev => ({ ...prev, period_start_date: e.target.value }))}
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="End Date"
                type="date"
                value={newProject.period_end_date}
                onChange={(e) => setNewProject(prev => ({ ...prev, period_end_date: e.target.value }))}
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button
            onClick={() => {
              setNewProject({
                project_name: '',
                project_description: '',
                project_type: 'CMPN_RSRC',
                period_start_date: new Date().toISOString().split('T')[0],
                period_end_date: new Date().toISOString().split('T')[0],
                period_type: 'daily',
                source: 'manual',
                active: true,
                status: 'active'
              });
              setProjectError('');
              setIsProjectDialogOpen(false);
            }}
            sx={{ color: '#64748B' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateProject}
            variant="contained"
            disabled={isSubmittingProject}
            sx={{
              backgroundColor: '#4c3794',
              '&:hover': { backgroundColor: '#388E3C' },
              fontWeight: 600
            }}
          >
            {isSubmittingProject ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                Creating...
              </>
            ) : (
              'Create Project'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Upload Document</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <input
              type="file"
              onChange={handleFileUpload}
              accept=".pdf,.doc,.docx,.txt"
              style={{ display: 'none' }}
              id="file-upload"
            />
            <label htmlFor="file-upload">
              <Button
                variant="outlined"
                component="span"
                startIcon={<UploadFileIcon />}
                fullWidth
                sx={{
                  py: 2,
                  borderColor: '#4c3794',
                  color: '#4c3794',
                  '&:hover': {
                    borderColor: '#3a2a73',
                    backgroundColor: 'rgba(76, 55, 148, 0.1)',
                  }
                }}
              >
                Choose File
              </Button>
            </label>
            <Button
              variant="outlined"
              startIcon={<AssessmentIcon />}
              fullWidth
              sx={{
                py: 2,
                borderColor: '#4c3794',
                color: '#4c3794',
                '&:hover': {
                  borderColor: '#3a2a73',
                  backgroundColor: 'rgba(76, 55, 148, 0.1)',
                },
                '&.Mui-disabled': {
                  borderColor: 'rgba(76, 55, 148, 0.3)',
                  color: 'rgba(76, 55, 148, 0.3)',
                }
              }}
              onClick={handleGoogleDriveAuth}
              disabled={googleDriveAuthState.loading}
            >
              {googleDriveAuthState.loading ? 'Connecting...' : 'Upload from Google Drive'}
            </Button>
            {uploadError && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {uploadError}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Google Drive Files Dialog */}
      <Dialog
        open={showGoogleDriveDialog}
        onClose={() => setShowGoogleDriveDialog(false)}
        maxWidth="md"
        fullWidth
        sx={{ '& .MuiDialog-paper': { height: '80vh' } }}
      >
        <DialogTitle sx={{ pb: 1, borderBottom: '1px solid #E5E9EF' }}>
          Select a file
        </DialogTitle>
        <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2, pt: 1 }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant={activeTab === 'myDrive' ? 'contained' : 'outlined'}
                onClick={() => handleGoogleDriveTabChange('myDrive')}
                size="small"
                sx={{
                  textTransform: 'none',
                  backgroundColor: activeTab === 'myDrive' ? '#9C27B0' : 'transparent',
                  color: activeTab === 'myDrive' ? 'white' : '#9C27B0',
                  borderColor: '#9C27B0',
                  '&:hover': {
                    backgroundColor: activeTab === 'myDrive' ? '#7B1FA2' : 'rgba(156, 39, 176, 0.1)',
                  },
                }}
              >
                My Drive
              </Button>
              <Button
                variant={activeTab === 'shared' ? 'contained' : 'outlined'}
                onClick={() => handleGoogleDriveTabChange('shared')}
                size="small"
                sx={{
                  textTransform: 'none',
                  backgroundColor: activeTab === 'shared' ? '#9C27B0' : 'transparent',
                  color: activeTab === 'shared' ? 'white' : '#9C27B0',
                  borderColor: '#9C27B0',
                  '&:hover': {
                    backgroundColor: activeTab === 'shared' ? '#7B1FA2' : 'rgba(156, 39, 176, 0.1)',
                  },
                }}
              >
                Shared with me
              </Button>
              <Button
                variant={activeTab === 'teamDrives' ? 'contained' : 'outlined'}
                onClick={() => handleGoogleDriveTabChange('teamDrives')}
                size="small"
                sx={{
                  textTransform: 'none',
                  backgroundColor: activeTab === 'teamDrives' ? '#9C27B0' : 'transparent',
                  color: activeTab === 'teamDrives' ? 'white' : '#9C27B0',
                  borderColor: '#9C27B0',
                  '&:hover': {
                    backgroundColor: activeTab === 'teamDrives' ? '#7B1FA2' : 'rgba(156, 39, 176, 0.1)',
                  },
                }}
              >
                Shared drives
              </Button>
            </Box>
          </Box>

          {/* Search Bar */}
          <Box sx={{ p: 2, borderBottom: '1px solid #E5E9EF' }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search files..."
              value={googleDriveSearchQuery}
              onChange={(e) => setGoogleDriveSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#9C27B0' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: '#9C27B0',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#9C27B0',
                  },
                },
              }}
            />
          </Box>

          {/* Folders Section */}
          <Box sx={{ px: 2, py: 1, borderBottom: '1px solid #E5E9EF' }}>
            <Typography variant="subtitle2" sx={{ color: '#666', mb: 1 }}>
              Folders
            </Typography>
            {googleDriveAuthState.loading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
                <CircularProgress size={16} sx={{ color: '#9C27B0' }} />
                <Typography variant="body2" sx={{ color: '#666' }}>
                  Loading folders...
                </Typography>
              </Box>
            ) : googleDriveFolders.length > 0 ? (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {googleDriveFolders.map((folder) => (
                  <Chip
                    key={folder.id}
                    icon={<FolderIcon />}
                    label={folder.name}
                    onClick={() => {
                      // Handle folder navigation if needed
                    }}
                    sx={{
                      backgroundColor: 'rgba(156, 39, 176, 0.1)',
                      color: '#9C27B0',
                      '&:hover': {
                        backgroundColor: 'rgba(156, 39, 176, 0.2)',
                      },
                    }}
                  />
                ))}
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2, color: '#666' }}>
                <FolderIcon sx={{ fontSize: 20 }} />
                <Typography variant="body2">No folders available</Typography>
              </Box>
            )}
          </Box>

          {/* Content Section */}
          <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
            {activeTab === 'teamDrives' ? (
              // Team Drives View
              <>
                <Typography variant="subtitle2" sx={{ color: '#666', mb: 2 }}>
                  Team Drives
                </Typography>
                {googleDriveAuthState.loading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 4 }}>
                    <CircularProgress size={20} sx={{ color: '#9C27B0' }} />
                    <Typography variant="body2" sx={{ color: '#666' }}>
                      Loading team drives...
                    </Typography>
                  </Box>
                ) : teamDrives.length > 0 ? (
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 2 }}>
                    {teamDrives.map((drive) => (
                      <Card
                        key={drive.id}
                        sx={{
                          p: 2,
                          cursor: 'pointer',
                          border: selectedTeamDrive === drive.id ? '2px solid #9C27B0' : '1px solid #e0e0e0',
                          '&:hover': {
                            boxShadow: '0 4px 12px rgba(156, 39, 176, 0.15)',
                          },
                        }}
                        onClick={() => handleTeamDriveSelect(drive.id)}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <AssessmentIcon sx={{ color: '#9C27B0' }} />
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {drive.name}
                          </Typography>
                        </Box>
                        <Typography variant="caption" sx={{ color: '#666' }}>
                          {drive.capabilities?.canAddChildren ? 'Can add files' : 'View only'}
                        </Typography>
                      </Card>
                    ))}
                  </Box>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 4, color: '#666' }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      🏢
                    </Typography>
                    <Typography variant="body2">No team drives available</Typography>
                  </Box>
                )}
              </>
            ) : (
              // Files View
              <>
                <Typography variant="subtitle2" sx={{ color: '#666', mb: 2 }}>
                  Files
                </Typography>
                {googleDriveAuthState.loading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 4 }}>
                    <CircularProgress size={20} sx={{ color: '#9C27B0' }} />
                    <Typography variant="body2" sx={{ color: '#666' }}>
                      Loading files...
                    </Typography>
                  </Box>
                ) : filteredGoogleDriveFiles.length > 0 ? (
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 2 }}>
                    {filteredGoogleDriveFiles.map((file) => (
                      <Card
                        key={file.id}
                        sx={{
                          p: 2,
                          cursor: 'pointer',
                          border: selectedGoogleDriveFiles.some(f => f.id === file.id) ? '2px solid #9C27B0' : '1px solid #e0e0e0',
                          '&:hover': {
                            boxShadow: '0 4px 12px rgba(156, 39, 176, 0.15)',
                          },
                        }}
                        onClick={() => handleGoogleDriveFileSelect(file)}
                      >
                        <Box sx={{ height: 80, mb: 1, position: 'relative', overflow: 'hidden', borderRadius: 1 }}>
                          {file.mimeType?.includes('image') ? (
                            <ImagePreview file={file} />
                          ) : (
                            <Box
                              sx={{
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: file.mimeType?.includes('pdf') ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' :
                                  file.mimeType?.includes('word') || file.mimeType?.includes('document') ? 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' :
                                    file.mimeType?.includes('sheet') || file.mimeType?.includes('excel') ? 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)' :
                                      file.mimeType?.includes('presentation') || file.mimeType?.includes('powerpoint') ? 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)' :
                                        'linear-gradient(135deg, #9C27B0 0%, #E1BEE7 100%)',
                                color: 'white',
                                borderRadius: '8px',
                                position: 'relative',
                                overflow: 'hidden',
                              }}
                            >
                              <Typography sx={{ fontSize: '24px', mb: 0.5 }}>
                                {getFileTypeIcon(file.mimeType)}
                              </Typography>
                              <Typography sx={{ fontSize: '10px', textAlign: 'center', px: 1 }}>
                                {getFileTypeLabel(file.mimeType)}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                        <Typography
                          variant="caption"
                          sx={{
                            display: 'block',
                            textAlign: 'center',
                            fontWeight: 500,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {file.name}
                        </Typography>
                      </Card>
                    ))}
                  </Box>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 4, color: '#666' }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      📄
                    </Typography>
                    <Typography variant="body2">
                      {googleDriveSearchQuery ? 'No files match your search' : 'No files available'}
                    </Typography>
                    {googleDriveSearchQuery && (
                      <Button
                        size="small"
                        onClick={() => setGoogleDriveSearchQuery('')}
                        sx={{ mt: 1, color: '#9C27B0' }}
                      >
                        Clear search
                      </Button>
                    )}
                  </Box>
                )}
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #E5E9EF' }}>
          <Button onClick={() => setShowGoogleDriveDialog(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleGoogleDriveUpload}
            variant="contained"
            disabled={selectedGoogleDriveFiles.length === 0 || uploadLoading}
            sx={{
              backgroundColor: '#9C27B0',
              '&:hover': { backgroundColor: '#7B1FA2' },
              '&.Mui-disabled': { backgroundColor: 'rgba(156, 39, 176, 0.3)' },
            }}
          >
            {uploadLoading ? 'Uploading...' : `Select (${selectedGoogleDriveFiles.length})`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Export Dialog - copied from SurveyInsights.tsx */}
      {showExportDialog && (
        <Dialog
          open={showExportDialog}
          onClose={handleCloseExportDialog}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: '16px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
              overflow: 'hidden'
            }
          }}
        >
          <DialogTitle sx={{
            backgroundColor: '#F8F9FA',
            borderBottom: '1px solid #E5E9EF',
            p: 3
          }}>
            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 2
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <DownloadIcon sx={{ color: '#4c3794', fontSize: 24 }} />
                <Typography variant="h6" sx={{
                  fontWeight: 600,
                  color: '#1a1a1a',
                  fontSize: '1.25rem'
                }}>
                  Export Content
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: '#64748B' }}>
                Step {currentStep} of 2
              </Typography>
            </Box>
          </DialogTitle>

          <DialogContent sx={{ p: 3 }}>
            {currentStep === 1 && (
              <Box sx={{ space: 3 }}>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ mb: 1, color: '#1a1a1a', fontWeight: 600 }}>
                    Brand Details
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#64748B' }}>
                    These details help us create content that matches your brand voice and style.
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {/* Company Website */}
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1, color: '#1a1a1a', fontWeight: 600 }}>
                      Company Website
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <TextField
                        fullWidth
                        type="url"
                        value={brandDetails.website}
                        onChange={(e) => handleBrandDetailsChange('website', e.target.value)}
                        disabled={brandDetails.websiteNotApplicable}
                        placeholder="https://www.example.com"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#4c3794'
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#4c3794'
                            }
                          }
                        }}
                      />
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <input
                          type="checkbox"
                          checked={brandDetails.websiteNotApplicable}
                          onChange={(e) => setBrandDetails(prev => ({ ...prev, websiteNotApplicable: e.target.checked }))}
                          style={{ accentColor: '#4c3794' }}
                        />
                        <Typography variant="body2" sx={{ color: '#64748B' }}>
                          Not applicable / No website
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  {/* Industry Keywords */}
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1, color: '#1a1a1a', fontWeight: 600 }}>
                      Industry Keywords
                    </Typography>
                    <TextField
                      fullWidth
                      value={brandDetails.industryKeywords}
                      onChange={(e) => handleBrandDetailsChange('industryKeywords', e.target.value)}
                      placeholder="fintech, banking, financial services, digital payments, public sector"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#4c3794'
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#4c3794'
                          }
                        }
                      }}
                    />
                  </Box>

                  {/* Target Audience */}
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1, color: '#1a1a1a', fontWeight: 600 }}>
                      Target Audience / Customer Segments
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {/* Selected audiences */}
                      {brandDetails.targetAudience.length > 0 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {brandDetails.targetAudience.map((audience) => (
                            <Chip
                              key={audience}
                              label={audience}
                              onDelete={() => handleRemoveAudience(audience)}
                              sx={{
                                backgroundColor: '#F3E5F5',
                                color: '#4c3794',
                                '& .MuiChip-deleteIcon': {
                                  color: '#4c3794',
                                  '&:hover': {
                                    color: '#7B1FA2'
                                  }
                                }
                              }}
                            />
                          ))}
                        </Box>
                      )}

                      {/* Add custom audience */}
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <TextField
                          fullWidth
                          size="small"
                          value={newAudience}
                          onChange={(e) => setNewAudience(e.target.value)}
                          placeholder="Add custom audience"
                          onKeyPress={(e) => e.key === 'Enter' && handleAddCustomAudience()}
                        />
                        <Button
                          onClick={handleAddCustomAudience}
                          size="small"
                          variant="outlined"
                          sx={{
                            borderColor: '#4c3794',
                            color: '#4c3794',
                            '&:hover': {
                              borderColor: '#7B1FA2',
                              backgroundColor: 'rgba(76, 55, 148, 0.05)'
                            }
                          }}
                        >
                          Add
                        </Button>
                      </Box>

                      {/* Audience options */}
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {audienceOptions.map((option) => (
                          <Chip
                            key={option}
                            label={option}
                            onClick={() => handleAudienceToggle(option)}
                            variant={brandDetails.targetAudience.includes(option) ? 'filled' : 'outlined'}
                            sx={{
                              backgroundColor: brandDetails.targetAudience.includes(option) ? '#F3E5F5' : 'transparent',
                              color: brandDetails.targetAudience.includes(option) ? '#4c3794' : '#64748B',
                              borderColor: '#4c3794',
                              '&:hover': {
                                backgroundColor: 'rgba(76, 55, 148, 0.05)'
                              }
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                  </Box>

                  {/* Brand Voice */}
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1, color: '#1a1a1a', fontWeight: 600 }}>
                      Brand Voice & Tone
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      value={brandDetails.brandVoice}
                      onChange={(e) => handleBrandDetailsChange('brandVoice', e.target.value)}
                      placeholder="bold, warm, authoritative, casual, formal, friendly, professional"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#4c3794'
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#4c3794'
                          }
                        }
                      }}
                    />
                    <Typography variant="caption" sx={{ color: '#64748B', mt: 0.5, display: 'block' }}>
                      Describe your brand's voice and tone. You can select specific tones for each content piece during generation.
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}

            {currentStep === 2 && (
              <Box sx={{ space: 3 }}>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ mb: 1, color: '#1a1a1a', fontWeight: 600 }}>
                    Content Preferences
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#64748B' }}>
                    Choose your preferred content format and style.
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {/* Content Category */}
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1, color: '#1a1a1a', fontWeight: 600 }}>
                      Content Category
                    </Typography>
                    <FormControl fullWidth>
                      <Select
                        value={contentPreferences.category}
                        onChange={(e) => handleContentPreferenceChange('category', e.target.value)}
                        sx={{
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#E0E0E0'
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#4c3794'
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#4c3794'
                          }
                        }}
                      >
                        {contentTypes.map((contentType) => (
                          <MenuItem key={contentType.contentType} value={contentType.contentType}>
                            {contentType.contentTypeDescription}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>

                  {/* Content Format */}
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1, color: '#1a1a1a', fontWeight: 600 }}>
                      Content Format
                    </Typography>
                    <FormControl fullWidth>
                      <Select
                        value={contentPreferences.format}
                        onChange={(e) => handleContentPreferenceChange('format', e.target.value)}
                        sx={{
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#E0E0E0'
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#4c3794'
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#4c3794'
                          }
                        }}
                      >
                        <MenuItem value="Text Only">Text Only</MenuItem>
                        <MenuItem value="Image Only">Image Only</MenuItem>
                        <MenuItem value="Infographic">Infographic</MenuItem>
                        <MenuItem value="Video">Video</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>

                  {/* Content Length */}
                  {isContentLengthApplicable() && (
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1, color: '#1a1a1a', fontWeight: 600 }}>
                        Content Length
                      </Typography>
                      <FormControl fullWidth>
                        <Select
                          value={contentPreferences.length}
                          onChange={(e) => handleContentPreferenceChange('length', e.target.value)}
                          sx={{
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#E0E0E0'
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#4c3794'
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#4c3794'
                            }
                          }}
                        >
                          <MenuItem value="Short (100-200 words)">Short (100-200 words)</MenuItem>
                          <MenuItem value="Medium (300-500 words)">Medium (300-500 words)</MenuItem>
                          <MenuItem value="Long (600+ words)">Long (600+ words)</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                  )}

                  {/* Writing Style */}
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1, color: '#1a1a1a', fontWeight: 600 }}>
                      Writing Style
                    </Typography>
                    <FormControl fullWidth>
                      <Select
                        value={contentPreferences.style}
                        onChange={(e) => handleContentPreferenceChange('style', e.target.value)}
                        sx={{
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#E0E0E0'
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#4c3794'
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#4c3794'
                          }
                        }}
                      >
                        <MenuItem value="Professional">Professional</MenuItem>
                        <MenuItem value="Casual">Casual</MenuItem>
                        <MenuItem value="Technical">Technical</MenuItem>
                        <MenuItem value="Creative">Creative</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>

                  {/* Additional Instructions */}
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1, color: '#1a1a1a', fontWeight: 600 }}>
                      Additional Instructions (Optional)
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      value={contentPreferences.instructions}
                      onChange={(e) => handleContentPreferenceChange('instructions', e.target.value)}
                      placeholder="Don't use emojis, don't use em-dash, avoid technical jargon, include call-to-action..."
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#4c3794'
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#4c3794'
                          }
                        }
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            )}
          </DialogContent>

          <DialogActions sx={{ p: 3, borderTop: '1px solid #E5E9EF' }}>
            <Button
              onClick={handleCloseExportDialog}
              sx={{ color: '#64748B' }}
            >
              Cancel
            </Button>

            <Box sx={{ display: 'flex', gap: 2 }}>
              {currentStep > 1 && (
                <Button
                  onClick={handlePrevStep}
                  variant="outlined"
                  sx={{
                    borderColor: '#4c3794',
                    color: '#4c3794',
                    '&:hover': {
                      borderColor: '#7B1FA2',
                      backgroundColor: 'rgba(76, 55, 148, 0.05)'
                    }
                  }}
                >
                  Previous
                </Button>
              )}

              {currentStep < 2 ? (
                <Button
                  onClick={handleNextStep}
                  variant="contained"
                  sx={{
                    backgroundColor: '#4c3794',
                    '&:hover': {
                      backgroundColor: '#3d2e7a'
                    }
                  }}
                >
                  Next
                </Button>
              ) : (
                <Button
                  onClick={handleGenerateContent}
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  sx={{
                    backgroundColor: '#4c3794',
                    '&:hover': {
                      backgroundColor: '#3d2e7a'
                    }
                  }}
                >
                  Generate Content
                </Button>
              )}
            </Box>
          </DialogActions>
        </Dialog>
      )}

      {/* Generated Content Display Dialog - copied from SurveyInsights.tsx */}
      {showContentDisplay && generatedContent && (
        <Dialog
          open={showContentDisplay}
          onClose={handleCloseContentDisplay}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: '16px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
              overflow: 'hidden'
            }
          }}
        >
          <DialogTitle sx={{
            backgroundColor: '#F8F9FA',
            borderBottom: '1px solid #E5E9EF',
            p: 3
          }}>
            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 2
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{
                  width: 32,
                  height: 32,
                  backgroundColor: '#4c3794',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Typography sx={{ color: 'white', fontSize: '16px' }}>✓</Typography>
                </Box>
                <Box>
                  <Typography variant="h6" sx={{
                    fontWeight: 600,
                    color: '#1a1a1a',
                    fontSize: '1.25rem'
                  }}>
                    Generated Content
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#64748B' }}>
                    {generatedContent.category} • {generatedContent.format}
                  </Typography>
                </Box>
              </Box>
              <IconButton
                onClick={handleCloseContentDisplay}
                sx={{
                  color: '#64748B',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)'
                  }
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>

          <DialogContent sx={{ p: 3 }}>
            {isGeneratingContent ? (
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '200px',
                gap: 2
              }}>
                <CircularProgress size={40} sx={{ color: '#4c3794' }} />
                <Typography variant="h6" sx={{ color: '#1a1a1a', fontWeight: 600 }}>
                  Generating Content...
                </Typography>
                <Typography variant="body2" sx={{ color: '#64748B', textAlign: 'center' }}>
                  Please wait while we create your content based on the selected conversation pairs.
                </Typography>
              </Box>
            ) : (['Image Only', 'Infographic'].includes(generatedContent.format)) ? (
              <Box sx={{
                backgroundColor: '#F8F9FA',
                borderRadius: '12px',
                p: 3,
                border: '1px solid #E5E9EF',
                textAlign: 'center'
              }}>
                <Typography variant="h6" sx={{ mb: 2, color: '#1a1a1a', fontWeight: 600 }}>
                  {generatedContent.title}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                  <img
                    src={generatedContent.content}
                    alt="Generated content visualization"
                    style={{
                      maxWidth: '100%',
                      height: 'auto',
                      borderRadius: '8px',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                      maxHeight: '500px'
                    }}
                  />
                </Box>
                <Typography variant="body2" sx={{ color: '#64748B' }}>
                  Generated {generatedContent.format.toLowerCase()} with charts and metrics based on your survey data and brand preferences
                </Typography>
              </Box>
            ) : (
              <Box sx={{
                backgroundColor: '#F8F9FA',
                borderRadius: '12px',
                p: 3,
                border: '1px solid #E5E9EF'
              }}>
                <Typography variant="h6" sx={{ mb: 2, color: '#1a1a1a', fontWeight: 600 }}>
                  {generatedContent.title}
                </Typography>

                {/* Main Content */}
                <Box sx={{
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  p: 2,
                  border: '1px solid #E0E0E0',
                  mb: 2
                }}>
                  <MarkdownMessage text={generatedContent.content} />
                </Box>

                {/* Visualization */}
                {generatedContent.visualization && (
                  <Box sx={{ mb: 2 }}>
                    {generatedContent.visualization.type === 'table' && (
                      <DataTable data={generatedContent.visualization.data} />
                    )}
                    {generatedContent.visualization.type === 'chart' && (
                      <DataChart data={generatedContent.visualization.data} />
                    )}
                    {generatedContent.visualization.type === 'heatmap' && (
                      <DataHeatmap data={generatedContent.visualization.data} />
                    )}
                  </Box>
                )}

                {/* Follow-up Questions */}
                {generatedContent.followupQuestions && generatedContent.followupQuestions.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, color: '#1a1a1a', fontWeight: 600 }}>
                      Follow-up Questions
                    </Typography>
                    <Box sx={{
                      backgroundColor: 'white',
                      borderRadius: '8px',
                      p: 2,
                      border: '1px solid #E0E0E0'
                    }}>
                      {generatedContent.followupQuestions.map((question, index) => (
                        <Typography key={index} variant="body2" sx={{ mb: 1, color: '#1a1a1a' }}>
                          • {question}
                        </Typography>
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Source Links */}
                {generatedContent.sourceLinks && generatedContent.sourceLinks.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, color: '#1a1a1a', fontWeight: 600 }}>
                      Source Links
                    </Typography>
                    <Box sx={{
                      backgroundColor: 'white',
                      borderRadius: '8px',
                      p: 2,
                      border: '1px solid #E0E0E0'
                    }}>
                      {generatedContent.sourceLinks.map((link, index) => (
                        <Typography key={index} variant="body2" sx={{ mb: 1, color: '#4c3794' }}>
                          <a href={link} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'none' }}>
                            {link}
                          </a>
                        </Typography>
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            )}
          </DialogContent>

          <DialogActions sx={{ p: 3, borderTop: '1px solid #E5E9EF' }}>
            <Button
              onClick={handleCloseContentDisplay}
              sx={{ color: '#64748B' }}
            >
              Close
            </Button>

            <Box sx={{ display: 'flex', gap: 2 }}>
              {/* Copy button for text-based content */}
              {(generatedContent.format === 'Text Only' || generatedContent.format === 'Video') && (
                <Button
                  onClick={handleCopyContent}
                  variant="outlined"
                  startIcon={<ContentCopyIcon />}
                  sx={{
                    borderColor: '#4c3794',
                    color: '#4c3794',
                    '&:hover': {
                      borderColor: '#7B1FA2',
                      backgroundColor: 'rgba(76, 55, 148, 0.05)'
                    }
                  }}
                >
                  Copy Content
                </Button>
              )}

              <Button
                onClick={handleDownloadContent}
                variant="contained"
                startIcon={<DownloadIcon />}
                sx={{
                  backgroundColor: '#4c3794',
                  '&:hover': {
                    backgroundColor: '#7B1FA2'
                  }
                }}
              >
                Download {
                  generatedContent.format === 'Image Only' || generatedContent.format === 'Infographic' ? 'PNG' :
                    generatedContent.format === 'Video' ? 'MP4' :
                      'Content'
                }
              </Button>
            </Box>
          </DialogActions>
        </Dialog>
      )}

      {/* Success/Error Snackbars */}
      <Snackbar open={!!success} autoHideDuration={6000} onClose={() => setSuccess(null)}>
        <Alert onClose={() => setSuccess(null)} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>
      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError(null)}>
        <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      {/* Floating Chat Button - Only show when chat is not open and not in fullscreen */}
      {!isChatOpen && !isFullScreen && (
        <Fab
          color="primary"
          aria-label="Open AI chat assistant"
          onClick={handleChatClick}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            backgroundColor: '#4c3794',
            '&:hover': {
              backgroundColor: '#3d2e7a',
            },
            '&:active': {
              transform: 'scale(0.95)'
            },
            zIndex: 1000,
          }}
        >
          <img src={botIcon} alt="AI Chat Assistant" style={{ width: 74, height: 74 }} />
        </Fab>
      )}
    </Box>
  );
};

export default CreditChecker;